import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Box,
    Grid,
    Stack,
    Typography,
    IconButton,
    TextField,
    Button,
    MenuItem,
    Slider,
    Divider,
    Autocomplete,
    Link,
} from '@mui/material';
import {
    EditRounded,
    CheckCircleOutline,
    Cancel,
    AddCircleOutline,
    ArrowDropDown,
    LocationOn,
} from '@mui/icons-material';
import { IWorkProfile, TProfileType, TSkill } from '../../types/global/helper';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { ThemeStyle } from '../../module/ThemeManager';
import LocationAutocomplete from '../LocationAutocomplete/LocationAutocomplete';
import { CORE_SKILLS, ETHNICITIES, LANGUAGES } from '../FindTalentsContainer/filterEnums';
import { SocialMediaSection } from './SocialMediaSection';
import { RichTextViewer } from '../Common/RichTextViewer';
import { RichTextEditor } from '../Common/RichTextEditor';
import { workProfileSectionTitleSx } from '../../constants/workProfileSectionTitle';
import { useWorkProfileEditFocus } from './WorkProfileEditFocusContext';
import { formatWorkProfileLocationLine } from '../../utils/AppUtils';

export interface ICastingDetails {
    profile: IWorkProfile;
    themeStyle: ThemeStyle;
    isEditMode?: boolean;
    onEditClick?: VoidFunction;
    previewMode?: boolean;
    onSubmitForm?: (data: any) => void;
    /** CastingNoir-style cards, eyebrows, merged title/about; use with technical profile + 8/4 column layout */
    technicalLayout?: boolean;
    /** Optional section-level filtering for layout composition in parent */
    allowedSections?: string[];
    /** Wrap selected sections in one card instead of per-section cards */
    singleCardSections?: boolean;
    /** When true with technicalLayout, skip the per-section outer card on direct child Boxes (e.g. Social Media block). */
    suppressDirectChildCard?: boolean;
    /** Artist sidebar: location as plain label + text (no pin / nested inner card). */
    artistPlainLocation?: boolean;
    /** Artist “About Me” eyebrow: larger than default technical 10px label. */
    artistStudioAboutHeader?: boolean;
    /** Performer cinematic page: large about copy, expertise bento grid, merged skills view */
    performerCinematicLayout?: boolean;
    /** When true with skills section: one “Expertise & Skills” grid combining primary + other skills (view mode). */
    mergeExpertiseSkills?: boolean;
    /** Performer sidebar: yellow eyebrow above the first grouped card (e.g. “Core Identity”). */
    performerSidebarEyebrow?: string;
    /** Performer sidebar: tighter vertical gaps between stage / age / location / attributes rows. */
    performerSidebarCompact?: boolean;
    /** Artist: merge first language / ethnicity / first special skill into About card; overflow as separate cards. */
    artistMergedAboutMeta?: boolean;
    /** Unique id for single-active-section edit focus across sibling CastingDetails (work profile pages). */
    editFocusScope?: string;
}

const ATTR_BODY_TYPES = ['athletic', 'thin', 'curvy', 'average', 'any'];

const MAX_LENGTH = 40;

type SectionKey = keyof IWorkProfile | 'age_range' | 'attributes';

// Define the hidden fields per profile type
const hiddenFieldsByProfileType: Record<TProfileType, (keyof IWorkProfile)[]> = {
    technical: ['stage_name', 'ethnicity'],
    content_creator: [], // add rules later if needed
    voiceover: [],
    performer: [],
    artist: [],
};

const hiddenSectionsByProfileType: Record<TProfileType, string[]> = {
    technical: ['age_range', 'attributes'], // 👈 exclude "age_range" and "attributes" section for content creators
    content_creator: ['age_range', 'attributes'],
    voiceover: [],
    performer: [],
    artist: [],
};

const TECH_SURFACE = '#1a1a1a';
const TECH_BORDER = '1px solid rgba(255,255,255,0.06)';
const TECH_RADIUS = '16px';
/** Physical attribute cells (artist / technical sidebar) — pic 1 black tiles */
const ATTR_TILE_BG = '#000000';
const ATTR_TILE_BORDER = '1px solid rgba(255,255,255,0.06)';

export const CastingDetails: React.FC<ICastingDetails> = ({
    profile,
    themeStyle,
    isEditMode = false,
    previewMode = true,
    onSubmitForm,
    technicalLayout = false,
    allowedSections,
    singleCardSections = false,
    suppressDirectChildCard = false,
    artistPlainLocation = false,
    artistStudioAboutHeader = false,
    performerCinematicLayout = false,
    mergeExpertiseSkills = false,
    performerSidebarEyebrow,
    performerSidebarCompact = false,
    artistMergedAboutMeta = false,
    editFocusScope,
}) => {
    // Section edit states
    const [editingSections, setEditingSections] = useState<{ [key: string]: boolean }>({});
    // Local state for editing
    const [about, setAbout] = useState(profile?.about || '');
    const [title, setTitle] = useState(profile?.title || '');
    const [stageName, setStageName] = useState(profile?.stage_name || '');
    const [ageRange, setAgeRange] = useState<[number, number]>([profile?.age_from || 18, profile?.age_to || 40]);
    const [location, setLocation] = useState(
        profile?.location || { address: '', description: '', coordinates: [], type: '', short_name: '' },
    );
    const [attributes, setAttributes] = useState({
        gender: profile?.gender || '',
        sex: profile?.sex || '',
        body_type: profile?.body_type || '',
        hair_color: profile?.hair_color || '',
        height: profile?.height || 0,
        weight: profile?.weight || 0,
    });
    // Skills/Other Skills state
    const [skills, setSkills] = useState<TSkill[]>(profile?.skills || []);
    const [otherSkills, setOtherSkills] = useState<TSkill[]>(profile?.other_skills || []);
    const [newSkill, setNewSkill] = useState<TSkill>({
        skill: '',
        expertise: 'beginner',
        years_of_experience: 1,
        description: '',
    });
    const [editingSkillIndex, setEditingSkillIndex] = useState<number | null>(null);
    const [languages, setLanguages] = useState<string[]>(profile?.language_spoken || []);
    const [newLanguage, setNewLanguage] = useState('');
    const [ethnicity, setEthnicity] = useState<string[]>(profile?.ethnicity || []);
    const [newEthnicity, setNewEthnicity] = useState('');
    const [specialSkills, setSpecialSkills] = useState<string[]>(profile?.special_skills || []);
    const [newSpecialSkill, setNewSpecialSkill] = useState('');
    const [links, setLinks] = useState<{ link: string; description: string }[]>(profile?.links || []);
    const [newLink, setNewLink] = useState<{ link: string; description: string }>({ link: '', description: '' });
    const [showAddSkillForm, setShowAddSkillForm] = useState(false);
    const [showAddOtherSkillForm, setShowAddOtherSkillForm] = useState(false);
    const [showAddLinkForm, setShowAddLinkForm] = useState(false);
    const [showAddSpecialSkillForm, setShowAddSpecialSkillForm] = useState(false);
    /** Artist merged About card: expand extra languages / ethnicity / special skills past first two lines. */
    const [artistAboutMetaListExpanded, setArtistAboutMetaListExpanded] = useState({
        languages: false,
        ethnicity: false,
        specialSkills: false,
    });
    const SKILLS = CORE_SKILLS[profile?.profile_type || 'performer'];

    // Add onSubmit handler (now only accepts values)
    const onSubmit = (values: any) => {
        onSubmitForm && onSubmitForm(values);
    };

    const handleSetProfile = useCallback(() => {
        if (profile) {
            setSkills(profile?.skills ?? []);
            setOtherSkills(profile?.other_skills ?? []);
            setLanguages(profile?.language_spoken ?? []);
            setEthnicity(profile?.ethnicity || []);
            setSpecialSkills(profile?.special_skills || []);
            setLinks(profile.links || []);
            setAttributes({
                gender: profile?.gender || '',
                sex: profile?.sex || '',
                body_type: profile?.body_type || '',
                hair_color: profile?.hair_color || '',
                height: profile?.height || 0,
                weight: profile?.weight || 0,
            });
            setAbout(profile?.about || '');
            setStageName(profile?.stage_name || '');
            setLocation(profile.location);
            setAgeRange([profile?.age_from || 18, profile?.age_to || 40]);
            setTitle(profile.title || '');
        }
    }, [profile]);

    useEffect(() => {
        handleSetProfile();
    }, [handleSetProfile]);

    // Implement getSectionValues helper to return the current values for each section
    const getSectionValues = (section: string) => {
        switch (section) {
            case 'about':
                return { about };
            case 'stage_name':
                return { stage_name: stageName };
            case 'age_range':
                return { age_from: ageRange[0], age_to: ageRange[1] };
            case 'location':
                return { location };
            case 'attributes':
                return { ...attributes };
            case 'skills':
                return { skills };
            case 'other_skills':
                return { other_skills: otherSkills };
            case 'languages':
                return { language_spoken: languages };
            case 'ethnicity':
                return { ethnicity };
            case 'special_skills':
                return { special_skills: specialSkills };
            case 'links':
                return { links };
            case 'title':
                return { title };
            default:
                return {};
        }
    };

    const editFocus = useWorkProfileEditFocus();

    const markSectionEditing = useCallback(
        (section: string, opening: boolean) => {
            if (opening) {
                if (editFocusScope && editFocus) {
                    editFocus.setActiveEditKey(`${editFocusScope}::${section}`);
                }
                setEditingSections({ [section]: true });
            } else {
                if (editFocusScope && editFocus && editFocus.activeEditKey === `${editFocusScope}::${section}`) {
                    editFocus.setActiveEditKey(null);
                }
                setEditingSections(prev => ({ ...prev, [section]: false }));
            }
        },
        [editFocus, editFocusScope],
    );

    useEffect(() => {
        if (!editFocusScope || !editFocus?.activeEditKey) return;
        const prefix = `${editFocusScope}::`;
        if (!editFocus.activeEditKey.startsWith(prefix)) {
            setEditingSections({});
        }
    }, [editFocus?.activeEditKey, editFocus, editFocusScope]);

    // Helper for rendering skill chips/fields
    const renderSkillChip = (
        skill: TSkill,
        idx: number,
        type: 'skills' | 'other_skills' = 'skills',
        editing = false,
    ) => {
        const expertise = (skill.expertise || 'beginner').toLowerCase();
        const levelMap: Record<string, number> = { beginner: 1, 'mid-level': 2, expert: 3 };
        const level = levelMap[expertise] || 1;
        const expertShort = expertise === 'expert';
        const midShort = expertise === 'mid-level';
        if (performerCinematicLayout && technicalLayout && !editing && type === 'skills') {
            const trackLabel = expertShort ? 'Expert focus' : midShort ? 'Professional' : 'Foundation';
            const pillLabel = expertShort ? 'Expert' : midShort ? 'Mid-Level' : 'Proficient';
            return (
                <Box
                    key={idx}
                    sx={{
                        width: '100%',
                        bgcolor: TECH_SURFACE,
                        borderRadius: '12px',
                        p: 3,
                        border: '1px solid rgba(255,255,255,0.05)',
                        transition: 'background-color 0.2s ease',
                        '&:hover': { bgcolor: '#202020' },
                    }}>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            gap: 2,
                        }}>
                        <Box sx={{ minWidth: 0 }}>
                            <Typography
                                sx={{
                                    fontSize: 11,
                                    color: 'rgba(255,255,255,0.55)',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.12em',
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    mb: 0.5,
                                }}>
                                {trackLabel}
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: { xs: '1.35rem', sm: '1.5rem' },
                                    fontWeight: 700,
                                    fontFamily: FONT_ENUM.BOLD_HEADER,
                                    lineHeight: 1.15,
                                    color: theme[themeStyle].true_white,
                                }}>
                                {skill.skill}
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                flexShrink: 0,
                                px: 1.25,
                                py: 0.5,
                                borderRadius: '6px',
                                bgcolor: expertShort ? 'rgba(250,175,63,0.2)' : 'rgba(255,255,255,0.08)',
                                color: expertShort ? theme[themeStyle].highlight : 'rgba(255,255,255,0.55)',
                                fontSize: 10,
                                fontWeight: 800,
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                textTransform: 'uppercase',
                                letterSpacing: '0.06em',
                            }}>
                            {pillLabel}
                        </Box>
                    </Box>
                </Box>
            );
        }
        if (technicalLayout && !editing && type === 'skills') {
            return (
                <Box
                    key={idx}
                    sx={{
                        width: '100%',
                        bgcolor: '#000000',
                        borderRadius: '14px',
                        px: 2.25,
                        py: 1.9,
                        border: '1px solid rgba(255,255,255,0.05)',
                    }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1.5 }}>
                        <Typography
                            sx={{
                                fontSize: 14,
                                color: theme[themeStyle].primary_white,
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                lineHeight: 1.2,
                            }}>
                            {skill.skill}
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: 11,
                                color: 'rgba(255,255,255,0.55)',
                                textTransform: 'uppercase',
                                letterSpacing: '0.08em',
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                flexShrink: 0,
                            }}>
                            {expertise}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mt: 1 }}>
                        {[1, 2, 3].map(i => (
                            <Box
                                key={i}
                                sx={{
                                    width: 52,
                                    height: 5,
                                    borderRadius: '999px',
                                    backgroundColor: i <= level ? theme[themeStyle].highlight : 'rgba(255,255,255,0.2)',
                                }}
                            />
                        ))}
                    </Box>
                </Box>
            );
        }
        if (technicalLayout && !editing && type === 'other_skills') {
            return (
        <Box
            key={idx}
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                        bgcolor: '#20201f',
                        borderRadius: '999px',
                        px: 1.5,
                        py: 0.75,
                mr: 1,
                mb: 1,
                        border: '1px solid rgba(255,255,255,0.08)',
            }}>
            <Typography
                        sx={{
                            fontSize: 12,
                            color: theme[themeStyle].primary_white,
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                        }}>
                {skill.skill}
            </Typography>
                </Box>
            );
        }
        if (technicalLayout && editing) {
            return (
                <Box
                    key={idx}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 1,
                        width: '100%',
                    }}>
                    <Typography
                        sx={{
                            fontSize: 13,
                            color: theme[themeStyle].primary_white,
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            flex: 1,
                        }}>
                        {skill.skill}
            </Typography>
                    <Typography
                        sx={{
                            fontSize: 11,
                            color: 'rgba(255,255,255,0.6)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.08em',
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                        }}>
                        {expertise}
                    </Typography>
                    <IconButton
                        size="small"
                        sx={{ ml: 0.5, p: 0.2 }}
                        onClick={() => {
                            setEditingSkillIndex(idx);
                            setNewSkill(skill);
                        }}>
                        <EditRounded fontSize="inherit" sx={{ color: theme[themeStyle].tag_white }} />
                    </IconButton>
                    <IconButton
                        size="small"
                        sx={{ ml: 0.2, p: 0.2, color: theme[themeStyle].highlight }}
                        onClick={() => {
                            if (type === 'skills') {
                                const newSkills = skills.filter((_, i) => i !== idx);
                                setSkills(newSkills);
                            } else {
                                const newOtherSkills = otherSkills.filter((_, i) => i !== idx);
                                setOtherSkills(newOtherSkills);
                            }
                        }}>
                        <Cancel fontSize="inherit" />
                    </IconButton>
                </Box>
            );
        }
        return (
            <Box
                key={idx}
                sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    bgcolor: '#20201f',
                    borderRadius: '999px',
                    px: 1.5,
                    py: 0.75,
                    mr: 1,
                    mb: 1,
                    border: '1px solid rgba(255,255,255,0.04)',
                }}>
                <Typography
                    sx={{
                        fontSize: 12,
                        color: theme[themeStyle].highlight,
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                    }}>
                    {skill.skill}
                </Typography>
                <Typography
                    sx={{
                        fontSize: 11,
                        color: '#a9a9a9',
                        ml: 1,
                        textTransform: 'uppercase',
                        letterSpacing: '0.08em',
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                    }}>
                    {expertise}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', ml: 1 }}>
                    {[1, 2, 3].map(i => (
                        <Box
                            key={i}
                            sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                backgroundColor: i <= level ? theme[themeStyle].highlight : '#5a5a5a',
                            }}
                        />
                    ))}
                </Box>
                {!previewMode && editing && (
                <IconButton
                    size="small"
                    sx={{ ml: 0.5, p: 0.2 }}
                    onClick={() => {
                        setEditingSkillIndex(idx);
                        setNewSkill(skill);
                    }}>
                    <EditRounded fontSize="inherit" sx={{ color: theme[themeStyle].tag_white }} />
                </IconButton>
            )}
            {!previewMode && editing && (
                <IconButton
                    size="small"
                    sx={{ ml: 0.2, p: 0.2, color: theme[themeStyle].highlight }}
                    onClick={() => {
                        if (type === 'skills') {
                            const newSkills = skills.filter((_, i) => i !== idx);
                            setSkills(newSkills);
                        } else {
                            const newOtherSkills = otherSkills.filter((_, i) => i !== idx);
                            setOtherSkills(newOtherSkills);
                        }
                    }}>
                    <Cancel fontSize="inherit" />
                </IconButton>
            )}
        </Box>
    );
    };

    const sleekPerformerTechnicalActions = profile?.profile_type === 'performer' && technicalLayout;
    const performerAttributesOnlyAccent =
        sleekPerformerTechnicalActions &&
        singleCardSections &&
        allowedSections?.length === 1 &&
        allowedSections[0] === 'attributes';
    const performerSidebarTight = performerSidebarCompact && technicalLayout;
    const sidebarSectionMb = performerSidebarTight ? 1 : 2;
    const sidebarDividerMy = performerSidebarTight ? 1 : 2;
    const performerColumnDensity = performerCinematicLayout && technicalLayout;
    const isVoiceoverProfile = profile?.profile_type === 'voiceover';

    const sleekTechCancelSx = {
        borderColor: 'rgba(255,255,255,0.12)',
        backgroundColor: 'rgba(255,255,255,0.04)',
        color: 'rgba(255,255,255,0.88)',
        fontSize: '0.75rem',
        minHeight: 30,
        minWidth: 76,
        px: 1.25,
        py: 0.5,
        borderRadius: '10px',
        textTransform: 'none' as const,
        fontWeight: 600,
        boxShadow: 'none',
    };
    const sleekTechSaveSx = {
        backgroundColor: theme[themeStyle].highlight,
        color: theme[themeStyle].black,
        fontSize: '0.75rem',
        minHeight: 30,
        minWidth: 72,
        px: 1.25,
        py: 0.5,
        borderRadius: '10px',
        textTransform: 'none' as const,
        fontWeight: 600,
        boxShadow: 'none',
    };

    // Section header (move this here, above return)
    const SectionHeader = ({
        title,
        section,
        labelVariant,
        suppressActionsInEdit = false,
        eyebrowEmphasis,
    }: {
        title: string;
        section: string;
        /** When technicalLayout: orange = primary accent label, muted = grey uppercase */
        labelVariant?: 'orange' | 'muted';
        /** When true and currently editing this section, render only the title (actions rendered below field). */
        suppressActionsInEdit?: boolean;
        /** Larger eyebrow for artist About Me (technical layout). */
        eyebrowEmphasis?: 'artistAbout' | 'performerAbout';
    }) => {
        const hideActionsNow = suppressActionsInEdit && !!editingSections[section];
        const lv = technicalLayout ? labelVariant || 'muted' : undefined;
        const useStackedActions = technicalLayout;
        const artistAboutEyebrow = eyebrowEmphasis === 'artistAbout' && title === 'About Me';
        const performerAboutEyebrow = eyebrowEmphasis === 'performerAbout' && title === 'About Me';
        if (technicalLayout && lv) {
            return (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: useStackedActions && editingSections[section] ? 'flex-start' : 'center',
                        flexDirection: useStackedActions && editingSections[section] ? 'column' : 'row',
                        mb: performerAboutEyebrow ? (sleekPerformerTechnicalActions ? 2 : 3) : 1,
                        width: '100%',
                        gap: useStackedActions && editingSections[section] ? 1 : 0,
                    }}>
                    <Typography
                        sx={{
                            fontSize: performerAboutEyebrow ? 12 : artistAboutEyebrow ? 13 : 10,
                            fontWeight: 700,
                            letterSpacing: performerAboutEyebrow ? '0.3em' : artistAboutEyebrow ? '0.28em' : '0.14em',
                            textTransform: 'uppercase',
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            color:
                                lv === 'orange' || performerAboutEyebrow
                                    ? theme[themeStyle].highlight
                                    : 'rgba(255,255,255,0.45)',
                        }}>
                        {title}
                    </Typography>
                    {!hideActionsNow &&
                        !previewMode &&
                        isEditMode &&
                        (editingSections[section] ? (
                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: sleekPerformerTechnicalActions ? 0.75 : 1,
                                    flexWrap: 'nowrap',
                                    flexDirection: useStackedActions ? 'column' : 'row',
                                    justifyContent: 'flex-start',
                                    width: useStackedActions
                                        ? { xs: '100%', sm: sleekPerformerTechnicalActions ? 196 : 230 }
                                        : 'auto',
                                }}>
                                <Button
                                    variant="outlined"
                                    size={sleekPerformerTechnicalActions ? 'small' : 'medium'}
                                    startIcon={sleekPerformerTechnicalActions ? undefined : <Cancel />}
                                    onClick={() => {
                                        switch (section) {
                                            case 'about':
                                                setAbout(profile?.about || '');
                                                break;
                                            case 'stage_name':
                                                setStageName(profile?.stage_name || '');
                                                break;
                                            case 'age_range':
                                                setAgeRange([profile?.age_from || 18, profile?.age_to || 40]);
                                                break;
                                            case 'location':
                                                setLocation(
                                                    profile?.location || {
                                                        address: '',
                                                        description: '',
                                                        coordinates: [],
                                                        type: '',
                                                        short_name: '',
                                                    },
                                                );
                                                break;
                                            case 'attributes':
                                                setAttributes({
                                                    gender: profile?.gender || '',
                                                    sex: profile?.sex || '',
                                                    body_type: profile?.body_type || '',
                                                    hair_color: profile?.hair_color || '',
                                                    height: profile?.height || 0,
                                                    weight: profile?.weight || 0,
                                                });
                                                break;
                                            case 'skills':
                                                setSkills(profile?.skills || []);
                                                break;
                                            case 'other_skills':
                                                setOtherSkills(profile?.other_skills || []);
                                                break;
                                            case 'languages':
                                                setLanguages(profile?.language_spoken || []);
                                                break;
                                            case 'ethnicity':
                                                setEthnicity(profile?.ethnicity || []);
                                                break;
                                            case 'special_skills':
                                                setSpecialSkills(profile?.special_skills || []);
                                                break;
                                            case 'links':
                                                setLinks(profile?.links || []);
                                                break;
                                            case 'title':
                                                setTitle(profile?.title || '');
                                                break;
                                        }
                                        markSectionEditing(section, false);
                                    }}
                                    sx={{
                                        ...(sleekPerformerTechnicalActions
                                            ? sleekTechCancelSx
                                            : {
                                                  borderColor: 'rgba(255,255,255,0.22)',
                                                  backgroundColor: '#20201f',
                                                  color: theme[themeStyle].primary_white,
                                                  fontSize: '0.85rem',
                                                  minHeight: 38,
                                                  minWidth: 108,
                                                  px: 1.75,
                                              }),
                                        whiteSpace: 'nowrap',
                                        width: useStackedActions ? '100%' : 'auto',
                                        '&:hover': sleekPerformerTechnicalActions
                                            ? {
                                                  borderColor: 'rgba(255,255,255,0.22)',
                                                  backgroundColor: 'rgba(255, 255, 255, 0.08)',
                                              }
                                            : {
                                                  borderColor: 'rgba(255,255,255,0.32)',
                                                  backgroundColor: 'rgba(255, 255, 255, 0.07)',
                                              },
                                    }}>
                                    Cancel
                                </Button>
                                <Button
                                    variant="contained"
                                    size={sleekPerformerTechnicalActions ? 'small' : 'medium'}
                                    startIcon={
                                        sleekPerformerTechnicalActions ? undefined : (
                                            <CheckCircleOutline sx={{ color: theme[themeStyle].black }} />
                                        )
                                    }
                                    onClick={() => {
                                        onSubmit(getSectionValues(section));
                                        markSectionEditing(section, false);
                                    }}
                                    sx={{
                                        ...(sleekPerformerTechnicalActions
                                            ? sleekTechSaveSx
                                            : {
                                                  backgroundColor: theme[themeStyle].highlight,
                                                  color: theme[themeStyle].black,
                                                  fontSize: '0.85rem',
                                                  minHeight: 38,
                                                  minWidth: 100,
                                                  px: 1.75,
                                              }),
                                        whiteSpace: 'nowrap',
                                        width: useStackedActions ? '100%' : 'auto',
                                        '&:hover': {
                                            backgroundColor: theme[themeStyle].primary_orange,
                                            opacity: 0.92,
                                        },
                                    }}>
                                    Save
                                </Button>
                            </Box>
                        ) : (
                            <IconButton
                                size={sleekPerformerTechnicalActions ? 'small' : 'medium'}
                                onClick={() => markSectionEditing(section, true)}
                                sx={{
                                    color: theme[themeStyle].primary_orange,
                                    p: sleekPerformerTechnicalActions ? 0.35 : 0.5,
                                }}>
                                <EditRounded fontSize={sleekPerformerTechnicalActions ? 'inherit' : 'small'} />
                            </IconButton>
                        ))}
                </Box>
            );
        }
        return (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography
                sx={{
                    fontFamily: FONT_ENUM.MAIN_BOLD,
                    fontSize: '1rem',
                    color: theme[themeStyle].primary_white,
                }}>
                {title}
            </Typography>
                {!hideActionsNow &&
                    !previewMode &&
                    isEditMode &&
                (editingSections[section] ? (
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                        <Button
                            variant="outlined"
                            startIcon={<Cancel />}
                            onClick={() => {
                                // Revert back to profile values when canceling
                                switch (section) {
                                    case 'about':
                                        setAbout(profile?.about || '');
                                        break;
                                    case 'stage_name':
                                        setStageName(profile?.stage_name || '');
                                        break;
                                    case 'age_range':
                                        setAgeRange([profile?.age_from || 18, profile?.age_to || 40]);
                                        break;
                                    case 'location':
                                        setLocation(
                                            profile?.location || {
                                                address: '',
                                                description: '',
                                                coordinates: [],
                                                type: '',
                                                short_name: '',
                                            },
                                        );
                                        break;
                                    case 'attributes':
                                        setAttributes({
                                            gender: profile?.gender || '',
                                            sex: profile?.sex || '',
                                            body_type: profile?.body_type || '',
                                            hair_color: profile?.hair_color || '',
                                            height: profile?.height || 0,
                                            weight: profile?.weight || 0,
                                        });
                                        break;
                                    case 'skills':
                                        setSkills(profile?.skills || []);
                                        break;
                                    case 'other_skills':
                                        setOtherSkills(profile?.other_skills || []);
                                        break;
                                    case 'languages':
                                        setLanguages(profile?.language_spoken || []);
                                        break;
                                    case 'ethnicity':
                                        setEthnicity(profile?.ethnicity || []);
                                        break;
                                    case 'special_skills':
                                        setSpecialSkills(profile?.special_skills || []);
                                        break;
                                    case 'links':
                                        setLinks(profile?.links || []);
                                        break;
                                    case 'title':
                                        setTitle(profile?.title || '');
                                        break;
                                }
                                    markSectionEditing(section, false);
                            }}
                            sx={{
                                    borderColor: 'rgba(255,255,255,0.22)',
                                    backgroundColor: '#20201f',
                                color: theme[themeStyle].primary_white,
                                    fontSize: '0.85rem',
                                    minHeight: 38,
                                    minWidth: 108,
                                    px: 1.75,
                                    whiteSpace: 'nowrap',
                                '&:hover': {
                                        borderColor: 'rgba(255,255,255,0.32)',
                                        backgroundColor: 'rgba(255, 255, 255, 0.07)',
                                },
                            }}>
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<CheckCircleOutline sx={{ color: theme[themeStyle].black }} />}
                            onClick={() => {
                                onSubmit(getSectionValues(section));
                                    markSectionEditing(section, false);
                            }}
                            sx={{
                                backgroundColor: theme[themeStyle].highlight,
                                color: theme[themeStyle].black,
                                    fontSize: '0.85rem',
                                    minHeight: 38,
                                    minWidth: 100,
                                    px: 1.75,
                                    whiteSpace: 'nowrap',
                                '&:hover': {
                                    backgroundColor: theme[themeStyle].primary_orange,
                                    opacity: 0.9,
                                },
                            }}>
                            Save
                        </Button>
                    </Box>
                ) : (
                    <IconButton
                            onClick={() => markSectionEditing(section, true)}
                        sx={{ color: theme[themeStyle].primary_orange, p: 0.5 }}>
                        <EditRounded fontSize="small" />
                    </IconButton>
                ))}
        </Box>
    );
    };

    const renderProfileTitle = useMemo(() => {
        let titleName = '';
        switch (profile?.profile_type) {
            case 'artist':
                titleName = 'E.g Musician, Music Producer';
                break;
            case 'content_creator':
                titleName = 'E.g Infleuncer, Content Creator.';
                break;
            case 'performer':
                titleName = 'E.g Actor, Stage Performer.';
                break;
            case 'technical':
                titleName = 'E.g Editor, Director';
                break;
            case 'voiceover':
                titleName = 'E.g Voice Actor';
                break;
        }
        return titleName;
    }, [profile]);
    const formatCompactNumber = (num: number): string => {
        if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
        if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
        return `${num}`;
    };

    function shouldShowSection(
        profile: IWorkProfile | undefined,
        fieldKey: SectionKey,
        sectionName: string,
        isEditing: boolean,
        hasValue: boolean,
    ): boolean {
        if (!profile) return false;
        if (allowedSections && !allowedSections.includes(sectionName)) return false;

        const hiddenFields = hiddenFieldsByProfileType[profile.profile_type] || [];
        const hiddenSections = hiddenSectionsByProfileType[profile.profile_type] || [];

        if (fieldKey === 'age_range' || fieldKey === 'attributes') {
            return !hiddenSections.includes(sectionName) && (isEditing || hasValue);
        }

        return !hiddenFields.includes(fieldKey) && !hiddenSections.includes(sectionName) && (isEditing || hasValue);
    }

    const showTitleBlock = shouldShowSection(profile, 'title', 'title', editingSections.title, !!profile?.title);
    const showAboutBlock = shouldShowSection(profile, 'about', 'about', editingSections.about, !!profile?.about);
    const isContentCreatorTitleOnlyCard =
        technicalLayout &&
        profile?.profile_type === 'content_creator' &&
        !!allowedSections &&
        allowedSections.length === 1 &&
        allowedSections[0] === 'title';
    const socialsWithValues = (profile?.socials || []).filter(s => s.bio && s.followers > 0);
    const totalReach = socialsWithValues.reduce((sum, social) => sum + social.followers, 0);
    const avgEngagementRate =
        socialsWithValues.length > 0
            ? socialsWithValues.reduce((sum, social) => sum + social.engagement, 0) / socialsWithValues.length
            : 0;
    const isSingleSkillsCard =
        technicalLayout &&
        !!allowedSections &&
        allowedSections.length === 1 &&
        (allowedSections[0] === 'skills' || allowedSections[0] === 'other_skills');
    const renderInlineSectionActions = (section: string) =>
        !previewMode && isEditMode && editingSections[section] ? (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: sleekPerformerTechnicalActions ? 0.75 : 1.25,
                    mt: sleekPerformerTechnicalActions ? 1 : 1.25,
                }}>
                <Button
                    variant="outlined"
                    size={sleekPerformerTechnicalActions ? 'small' : 'medium'}
                    startIcon={sleekPerformerTechnicalActions ? undefined : <Cancel />}
                    onClick={() => {
                        switch (section) {
                            case 'title':
                                setTitle(profile?.title || '');
                                break;
                            case 'about':
                                setAbout(profile?.about || '');
                                break;
                            case 'skills':
                                setSkills(profile?.skills || []);
                                break;
                            case 'other_skills':
                                setOtherSkills(profile?.other_skills || []);
                                break;
                            case 'languages':
                                setLanguages(profile?.language_spoken || []);
                                break;
                            case 'special_skills':
                                setSpecialSkills(profile?.special_skills || []);
                                break;
                            case 'links':
                                setLinks(profile?.links || []);
                                break;
                            case 'ethnicity':
                                setEthnicity(profile?.ethnicity || []);
                                break;
                            case 'stage_name':
                                setStageName(profile?.stage_name || '');
                                break;
                            case 'age_range':
                                setAgeRange([profile?.age_from || 18, profile?.age_to || 40]);
                                break;
                            case 'attributes':
                                setAttributes({
                                    gender: profile?.gender || '',
                                    sex: profile?.sex || '',
                                    body_type: profile?.body_type || '',
                                    hair_color: profile?.hair_color || '',
                                    height: profile?.height || 0,
                                    weight: profile?.weight || 0,
                                });
                                break;
                        }
                        markSectionEditing(section, false);
                    }}
                    sx={{
                        ...(sleekPerformerTechnicalActions
                            ? sleekTechCancelSx
                            : {
                                  borderColor: 'rgba(255,255,255,0.22)',
                                  backgroundColor: '#20201f',
                                  color: theme[themeStyle].primary_white,
                                  fontSize: '0.85rem',
                                  minHeight: 38,
                                  px: 1.75,
                              }),
                        whiteSpace: 'nowrap',
                        width: '100%',
                        '&:hover': sleekPerformerTechnicalActions
                            ? {
                                  borderColor: 'rgba(255,255,255,0.22)',
                                  backgroundColor: 'rgba(255, 255, 255, 0.08)',
                              }
                            : undefined,
                    }}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    size={sleekPerformerTechnicalActions ? 'small' : 'medium'}
                    startIcon={
                        sleekPerformerTechnicalActions ? undefined : (
                            <CheckCircleOutline sx={{ color: theme[themeStyle].black }} />
                        )
                    }
                    onClick={() => {
                        onSubmit(getSectionValues(section));
                        markSectionEditing(section, false);
                    }}
                    sx={{
                        ...(sleekPerformerTechnicalActions
                            ? sleekTechSaveSx
                            : {
                                  backgroundColor: theme[themeStyle].highlight,
                                  color: theme[themeStyle].black,
                                  fontSize: '0.85rem',
                                  minHeight: 38,
                                  px: 1.75,
                              }),
                        whiteSpace: 'nowrap',
                        width: '100%',
                        '&:hover': {
                            backgroundColor: theme[themeStyle].primary_orange,
                            opacity: 0.92,
                        },
                    }}>
                    Save
                </Button>
            </Box>
        ) : null;

    const ARTIST_ABOUT_META_VISIBLE = 2;

    const renderArtistMergedAboutMetaRow = (marginTop: number) => {
        const valueLineSx = {
            fontSize: 14,
            fontFamily: FONT_ENUM.MAIN_BOLD,
            color: theme[themeStyle].primary_white,
        };
        const emptyLineSx = {
            ...valueLineSx,
            color: 'rgba(255,255,255,0.35)',
        };

        const renderStackedList = (
            items: string[],
            emptyLabel: string,
            capitalize: boolean,
            expanded: boolean,
            onToggle: () => void,
        ) => {
            if (items.length === 0) {
    return (
                    <Typography
                        sx={{
                            ...emptyLineSx,
                            textTransform: capitalize ? 'capitalize' : 'none',
                        }}>
                        {emptyLabel}
                    </Typography>
                );
            }
            const visible = expanded ? items : items.slice(0, ARTIST_ABOUT_META_VISIBLE);
            const extraCount = Math.max(0, items.length - ARTIST_ABOUT_META_VISIBLE);
            return (
                <Stack spacing={0.5}>
                    {visible.map((item, idx) => (
                        <Typography
                            key={`${item}-${idx}`}
                            sx={{
                                ...valueLineSx,
                                textTransform: capitalize ? 'capitalize' : 'none',
                            }}>
                            {item}
                        </Typography>
                    ))}
                    {extraCount > 0 && (
                        <Link
                            component="button"
                            type="button"
                            variant="body2"
                            onClick={onToggle}
                            sx={{
                                alignSelf: 'flex-start',
                                cursor: 'pointer',
                                border: 'none',
                                background: 'none',
                                padding: 0,
                                fontSize: 13,
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                color: theme[themeStyle].highlight,
                                textDecoration: 'underline',
                                '&:hover': { color: theme[themeStyle].primary_orange },
                            }}>
                            {expanded ? 'Hide' : `+ ${extraCount} more`}
                        </Link>
                    )}
                </Stack>
            );
        };

        return (
            <Box
                sx={{
                    mt: marginTop,
                    pt: 2.5,
                    borderTop: '1px solid rgba(255,255,255,0.06)',
                }}>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={0.5}>
                            <Typography
                                sx={{
                                    fontSize: 10,
                                    fontWeight: 700,
                                    letterSpacing: '0.12em',
                                    textTransform: 'uppercase',
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    color: 'rgba(255,255,255,0.45)',
                                    mb: 0.75,
                                }}>
                                Languages
                            </Typography>
                            {!previewMode && isEditMode && (
                                <IconButton
                                    size="small"
                                    onClick={() => markSectionEditing('languages', true)}
                                    sx={{ color: theme[themeStyle].primary_orange, p: 0.35, mt: -0.5 }}>
                                    <EditRounded fontSize="small" />
                                </IconButton>
                            )}
                        </Stack>
                        {renderStackedList(
                            languages,
                            'No languages',
                            true,
                            artistAboutMetaListExpanded.languages,
                            () =>
                                setArtistAboutMetaListExpanded(s => ({
                                    ...s,
                                    languages: !s.languages,
                                })),
                        )}
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={0.5}>
                            <Typography
                                sx={{
                                    fontSize: 10,
                                    fontWeight: 700,
                                    letterSpacing: '0.12em',
                                    textTransform: 'uppercase',
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    color: 'rgba(255,255,255,0.45)',
                                    mb: 0.75,
                                }}>
                                Ethnicity
                            </Typography>
                            {!previewMode && isEditMode && (
                                <IconButton
                                    size="small"
                                    onClick={() => markSectionEditing('ethnicity', true)}
                                    sx={{ color: theme[themeStyle].primary_orange, p: 0.35, mt: -0.5 }}>
                                    <EditRounded fontSize="small" />
                                </IconButton>
                            )}
                        </Stack>
                        {renderStackedList(
                            ethnicity,
                            'No ethnicity',
                            false,
                            artistAboutMetaListExpanded.ethnicity,
                            () =>
                                setArtistAboutMetaListExpanded(s => ({
                                    ...s,
                                    ethnicity: !s.ethnicity,
                                })),
                        )}
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={0.5}>
                            <Typography
                                sx={{
                                    fontSize: 10,
                                    fontWeight: 700,
                                    letterSpacing: '0.12em',
                                    textTransform: 'uppercase',
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    color: 'rgba(255,255,255,0.45)',
                                    mb: 0.75,
                                }}>
                                Special skills
                            </Typography>
                            {!previewMode && isEditMode && (
                                <IconButton
                                    size="small"
                                    onClick={() => markSectionEditing('special_skills', true)}
                                    sx={{ color: theme[themeStyle].primary_orange, p: 0.35, mt: -0.5 }}>
                                    <EditRounded fontSize="small" />
                                </IconButton>
                            )}
                        </Stack>
                        {renderStackedList(
                            specialSkills,
                            'None',
                            true,
                            artistAboutMetaListExpanded.specialSkills,
                            () =>
                                setArtistAboutMetaListExpanded(s => ({
                                    ...s,
                                    specialSkills: !s.specialSkills,
                                })),
                        )}
                    </Grid>
                </Grid>
            </Box>
        );
    };

    return (
        <Box
            sx={{
                py: 1,
                px: 0,
                bgcolor: 'transparent',
                borderRadius: 2,
                width: '100%',
                ...(singleCardSections
                    ? {
                          p: technicalLayout ? { xs: 2.5, sm: 3 } : { xs: 2, sm: 2.5 },
                          borderRadius: technicalLayout ? TECH_RADIUS : '14px',
                          backgroundColor: TECH_SURFACE,
                          border: TECH_BORDER,
                      }
                    : {}),
                ...(isSingleSkillsCard ? { height: '100%' } : {}),
                '& .MuiOutlinedInput-root': {
                    backgroundColor: '#131313',
                    '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255,255,255,0.14)',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255,255,255,0.22)',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme[themeStyle].highlight,
                    },
                },
                '& > .MuiBox-root': {
                    ...(singleCardSections || suppressDirectChildCard
                        ? {}
                        : {
                              mb: 2,
                              p: technicalLayout ? { xs: 2.5, sm: 3 } : { xs: 2, sm: 2.5 },
                              borderRadius: technicalLayout ? TECH_RADIUS : '14px',
                              backgroundColor: TECH_SURFACE,
                              border: TECH_BORDER,
                          }),
                },
                '& > .MuiDivider-root': {
                    display: 'none',
                },
            }}>
            {technicalLayout && singleCardSections && performerSidebarEyebrow ? (
                <Typography component="div" sx={{ ...workProfileSectionTitleSx(themeStyle), mb: 1.25, mt: 0 }}>
                    {performerSidebarEyebrow}
                </Typography>
            ) : null}
            {technicalLayout && (showTitleBlock || showAboutBlock) ? (
                <>
                    <Stack
                        sx={{
                            mb: performerColumnDensity ? 1.25 : 2,
                            p:
                                (performerCinematicLayout && showAboutBlock && !showTitleBlock) || isVoiceoverProfile
                                    ? 0
                                    : { xs: 2.5, sm: 4 },
                            borderRadius:
                                (performerCinematicLayout && showAboutBlock && !showTitleBlock) || isVoiceoverProfile
                                    ? 0
                                    : TECH_RADIUS,
                            bgcolor:
                                (performerCinematicLayout && showAboutBlock && !showTitleBlock) || isVoiceoverProfile
                                    ? 'transparent'
                                    : TECH_SURFACE,
                            border:
                                (performerCinematicLayout && showAboutBlock && !showTitleBlock) || isVoiceoverProfile
                                    ? 'none'
                                    : TECH_BORDER,
                            position: 'relative',
                        }}>
                        {showTitleBlock && (
                            <Box sx={{ mb: showAboutBlock ? 3 : 0 }}>
                                <SectionHeader
                                    title={isContentCreatorTitleOnlyCard ? 'Primary Title' : 'Title'}
                                    section="title"
                                    labelVariant={isContentCreatorTitleOnlyCard ? 'muted' : 'orange'}
                                    suppressActionsInEdit={technicalLayout}
                                />
                                {!editingSections.title || previewMode ? (
                                    <Typography
                                        sx={{
                                            fontFamily: FONT_ENUM.BOLD_HEADER,
                                            fontWeight: 800,
                                            color: theme[themeStyle].primary_white,
                                            fontSize: { xs: '1.35rem', sm: '1.5rem' },
                                            letterSpacing: '-0.02em',
                                            whiteSpace: 'pre-wrap',
                                        }}>
                                        {profile?.title || 'No title provided'}
                                    </Typography>
                                ) : (
                                    <TextField
                                        fullWidth
                                        placeholder={renderProfileTitle}
                                        value={title}
                                        onChange={e => setTitle(e.target.value)}
                                        inputProps={{
                                            maxLength: MAX_LENGTH,
                                        }}
                                        helperText={`${MAX_LENGTH - title.length} characters left`}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                color: theme[themeStyle].tag_white,
                                                backgroundColor: theme[themeStyle].gray_text,
                                                fontSize: 13,
                                            },
                                            '& .MuiFormHelperText-root': {
                                                color: theme[themeStyle].tag_white,
                                                fontSize: 11,
                                                textAlign: 'right',
                                            },
                                        }}
                                    />
                                )}
                                {isContentCreatorTitleOnlyCard && !editingSections.title && (
                                    <Box
                                        sx={{
                                            mt: 2.5,
                                            display: 'grid',
                                            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                                            gap: 1.5,
                                        }}>
                                        <Box
                                            sx={{
                                                p: { xs: 2, sm: 2.5 },
                                                borderRadius: '18px',
                                                bgcolor: '#000000',
                                            }}>
                                            <Typography
                                                sx={{
                                                    color: 'rgba(255,255,255,0.45)',
                                                    fontSize: 10,
                                                    fontWeight: 700,
                                                    letterSpacing: '0.12em',
                                                    textTransform: 'uppercase',
                                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                                    mb: 1.1,
                                                }}>
                                                Total Reach
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    color: theme[themeStyle].primary_white,
                                                    fontFamily: FONT_ENUM.BOLD_HEADER,
                                                    fontSize: { xs: '2rem', sm: '2.35rem' },
                                                    lineHeight: 1,
                                                    fontWeight: 800,
                                                }}>
                                                {formatCompactNumber(totalReach)}
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    mt: 0.8,
                                                    color: 'rgba(255,255,255,0.42)',
                                                    fontSize: 11,
                                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                                }}>
                                                Followers across all platforms
                                            </Typography>
                                        </Box>
                                        <Box
                                            sx={{
                                                p: { xs: 2, sm: 2.5 },
                                                borderRadius: '18px',
                                                bgcolor: '#000000',
                                            }}>
                                            <Typography
                                                sx={{
                                                    color: 'rgba(255,255,255,0.45)',
                                                    fontSize: 10,
                                                    fontWeight: 700,
                                                    letterSpacing: '0.12em',
                                                    textTransform: 'uppercase',
                                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                                    mb: 1.1,
                                                }}>
                                                Engagement
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    color: theme[themeStyle].highlight,
                                                    fontFamily: FONT_ENUM.BOLD_HEADER,
                                                    fontSize: { xs: '2rem', sm: '2.35rem' },
                                                    lineHeight: 1,
                                                    fontWeight: 800,
                                                }}>
                                                {avgEngagementRate.toFixed(1)}%
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    mt: 0.8,
                                                    color: 'rgba(255,255,255,0.42)',
                                                    fontSize: 11,
                                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                                }}>
                                                Average engagement rate
                                            </Typography>
                                        </Box>
                                    </Box>
                                )}
                                {renderInlineSectionActions('title')}
                            </Box>
                        )}
                        {showAboutBlock && (
                            <Box>
                                <SectionHeader
                                    title="About Me"
                                    section="about"
                                    labelVariant={performerCinematicLayout ? 'orange' : 'muted'}
                                    suppressActionsInEdit={technicalLayout}
                                    eyebrowEmphasis={
                                        performerCinematicLayout
                                            ? 'performerAbout'
                                            : artistStudioAboutHeader
                                            ? 'artistAbout'
                                            : undefined
                                    }
                                />
                                {!editingSections.about || previewMode ? (
                                    isVoiceoverProfile ? (
                                        <Box
                                            sx={{
                                                bgcolor: TECH_SURFACE,
                                                border: TECH_BORDER,
                                                borderLeft: `4px solid ${theme[themeStyle].highlight}`,
                                                borderRadius: '12px',
                                                px: { xs: 2, sm: 2.5 },
                                                py: { xs: 2, sm: 2.5 },
                                            }}>
                                            <RichTextViewer
                                                content={profile?.about || 'No about info provided'}
                                                textStyle={{
                                                    padding: 0,
                                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                                    color: 'rgba(255,255,255,0.75)',
                                                    fontSize: 20,
                                                    lineHeight: 1.75,
                                                    fontWeight: 300,
                                                    whiteSpace: 'pre-wrap',
                                                }}
                                            />
                                        </Box>
                                    ) : (
                                        <RichTextViewer
                                            content={profile?.about || 'No about info provided'}
                                            textStyle={{
                                                padding: 0,
                                                fontFamily: FONT_ENUM.MAIN_REGULAR,
                                                color: 'rgba(255,255,255,0.65)',
                                                fontSize: performerCinematicLayout ? 20 : 14,
                                                lineHeight: performerCinematicLayout ? 1.65 : 1.65,
                                                fontWeight: performerCinematicLayout ? 300 : undefined,
                                                whiteSpace: 'pre-wrap',
                                            }}
                                        />
                                    )
                                ) : (
                                    <RichTextEditor value={about} onChange={e => setAbout(e)} />
                                )}
                                {artistMergedAboutMeta &&
                                    !editingSections.languages &&
                                    !editingSections.ethnicity &&
                                    !editingSections.special_skills &&
                                    (!editingSections.about || previewMode) &&
                                    renderArtistMergedAboutMetaRow(3)}
                                {renderInlineSectionActions('about')}
                                {artistMergedAboutMeta &&
                                    !editingSections.languages &&
                                    !editingSections.ethnicity &&
                                    !editingSections.special_skills &&
                                    editingSections.about &&
                                    !previewMode &&
                                    renderArtistMergedAboutMetaRow(2)}
                            </Box>
                        )}
                    </Stack>
                </>
            ) : (
                <>
            {/** Title */}
                    {showTitleBlock && (
                <>
                    <Box sx={{ mb: 2 }}>
                        <SectionHeader title="Title" section="title" />
                        {!editingSections.title || previewMode ? (
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    color: theme[themeStyle].primary_white,
                                    fontSize: 13,
                                    whiteSpace: 'pre-wrap',
                                }}>
                                {profile?.title || 'No title provided'}
                            </Typography>
                        ) : (
                            <TextField
                                fullWidth
                                placeholder={renderProfileTitle}
                                value={title}
                                onChange={e => setTitle(e.target.value)}
                                inputProps={{
                                    maxLength: MAX_LENGTH,
                                }}
                                helperText={`${MAX_LENGTH - title.length} characters left`}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        color: theme[themeStyle].tag_white,
                                        backgroundColor: theme[themeStyle].gray_text,
                                        fontSize: 13,
                                    },
                                    '& .MuiFormHelperText-root': {
                                        color: theme[themeStyle].tag_white,
                                        fontSize: 11,
                                        textAlign: 'right',
                                    },
                                }}
                            />
                        )}
                    </Box>
                    <Divider sx={{ my: 2, borderColor: theme[themeStyle].grey }} />
                </>
            )}

            {/* About Section - Only show if value or in edit mode */}
                    {showAboutBlock && (
                <>
                    <Box sx={{ mb: 2 }}>
                        <SectionHeader title="About Me" section="about" />
                        {!editingSections.about || previewMode ? (
                            <RichTextViewer
                                content={profile?.about || 'No about info provided'}
                                textStyle={{
                                    padding: 0,
                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    color: theme[themeStyle].primary_white,
                                    fontSize: 13,
                                    whiteSpace: 'pre-wrap',
                                }}
                            />
                        ) : (
                            <RichTextEditor value={about} onChange={e => setAbout(e)} />
                        )}
                    </Box>
                    <Divider sx={{ my: 2, borderColor: theme[themeStyle].grey }} />
                        </>
                    )}
                </>
            )}

            {/* Social Media Section - Only for Content Creators, only show if values or in edit mode */}
            {shouldShowSection(
                profile,
                'socials',
                'socials',
                editingSections.socials,
                !!profile?.socials?.some(s => s.bio && s.followers > 0),
            ) && (
                <>
                    <SocialMediaSection
                        workProfile={profile}
                        themeStyle={themeStyle}
                        isEditMode={isEditMode && !previewMode}
                        previewMode={previewMode}
                        onSubmitForm={onSubmitForm}
                    />
                    <Divider sx={{ my: 2, borderColor: theme[themeStyle].grey }} />
                </>
            )}

            {/* Stage Name Section - Hide for Content Creators */}
            {shouldShowSection(
                profile,
                'stage_name',
                'stage_name',
                editingSections.stage_name,
                !!profile?.stage_name,
            ) && (
                <>
                    <Box sx={{ mb: sidebarSectionMb }}>
                        <SectionHeader
                            title="Stage Name"
                            section="stage_name"
                            suppressActionsInEdit={technicalLayout}
                        />
                        {!editingSections.stage_name || previewMode ? (
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    color: theme[themeStyle].primary_white,
                                    fontSize: 13,
                                }}>
                                {profile?.stage_name || 'No stage name provided'}
                            </Typography>
                        ) : (
                            <TextField
                                fullWidth
                                placeholder="Stage Name"
                                value={stageName}
                                helperText={`${MAX_LENGTH - stageName.length} characters left`}
                                inputProps={{
                                    maxLength: MAX_LENGTH,
                                }}
                                onChange={e => setStageName(e.target.value)}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        color: theme[themeStyle].tag_white,
                                        backgroundColor: theme[themeStyle].gray_text,
                                        fontSize: 13,
                                    },
                                    '& .MuiFormHelperText-root': {
                                        color: theme[themeStyle].tag_white,
                                        fontSize: 11,
                                        textAlign: 'right',
                                    },
                                }}
                            />
                        )}
                        {technicalLayout && renderInlineSectionActions('stage_name')}
                    </Box>
                    <Divider sx={{ my: sidebarDividerMy, borderColor: theme[themeStyle].grey }} />
                </>
            )}

            {/* Age Range Section - Hide for Content Creators */}
            {shouldShowSection(
                profile,
                'age_range',
                'age_range',
                editingSections.age_range,
                profile?.age_from !== undefined || profile?.age_to !== undefined,
            ) && (
                <>
                    <Box sx={{ mb: sidebarSectionMb }}>
                        <SectionHeader title="Age Range" section="age_range" suppressActionsInEdit={technicalLayout} />
                        {!editingSections.age_range || previewMode ? (
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    color: theme[themeStyle].primary_white,
                                    fontSize: 13,
                                }}>
                                {profile?.age_from} - {profile?.age_to}
                            </Typography>
                        ) : (
                            <Box sx={{ px: 1, py: 0.5 }}>
                                <Slider
                                    value={ageRange}
                                    onChange={(_, val) => setAgeRange(val as [number, number])}
                                    valueLabelDisplay="auto"
                                    min={0}
                                    max={100}
                                    sx={{
                                        color: theme[themeStyle].primary_orange,
                                        height: 2,
                                        '& .MuiSlider-thumb': {
                                            backgroundColor: theme[themeStyle].primary_orange,
                                            width: 16,
                                            height: 16,
                                        },
                                    }}
                                />
                                <Typography sx={{ color: theme[themeStyle].primary_white, fontSize: 13 }}>
                                    {ageRange[0]} - {ageRange[1]}
                                </Typography>
                            </Box>
                        )}
                        {technicalLayout && renderInlineSectionActions('age_range')}
                    </Box>
                    <Divider sx={{ my: sidebarDividerMy, borderColor: theme[themeStyle].grey }} />
                </>
            )}

            {/* Location Section */}
            {shouldShowSection(profile, 'location', 'location', editingSections.location, !!profile?.location) && (
                <>
                    {' '}
                    <Box
                        sx={{
                            mb: sidebarSectionMb,
                            ...(technicalLayout && !artistPlainLocation
                                ? {
                                      p: 3,
                                      borderRadius: TECH_RADIUS,
                                      bgcolor: TECH_SURFACE,
                                      border: TECH_BORDER,
                                      display: 'flex',
                                      flexDirection: editingSections.location ? 'column' : 'row',
                                      alignItems: editingSections.location ? 'stretch' : { xs: 'center', sm: 'center' },
                                      justifyContent: editingSections.location ? 'flex-start' : 'space-between',
                                      gap: 2,
                                      flexWrap: 'nowrap',
                                  }
                                : {}),
                        }}>
                        {technicalLayout && artistPlainLocation ? (
                            <>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        mb: 0.75,
                                        gap: 1,
                                    }}>
                                    <Typography
                                        sx={{
                                            fontSize: 10,
                                            fontWeight: 700,
                                            letterSpacing: '0.14em',
                                            textTransform: 'uppercase',
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            color: 'rgba(255,255,255,0.45)',
                                        }}>
                                        Location
                                    </Typography>
                                    {!previewMode && isEditMode && !editingSections.location && (
                                        <IconButton
                                            size={sleekPerformerTechnicalActions ? 'small' : 'medium'}
                                            onClick={() => markSectionEditing('location', true)}
                                            sx={{
                                                color: theme[themeStyle].primary_orange,
                                                p: sleekPerformerTechnicalActions ? 0.35 : 0.5,
                                                flexShrink: 0,
                                            }}>
                                            <EditRounded
                                                fontSize={sleekPerformerTechnicalActions ? 'inherit' : 'small'}
                                            />
                                        </IconButton>
                                    )}
                                </Box>
                                {!editingSections.location || previewMode ? (
                                    <Typography
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            color: theme[themeStyle].primary_white,
                                            fontSize: 15,
                                        }}>
                                        {formatWorkProfileLocationLine(profile?.location) || 'No location provided'}
                                    </Typography>
                                ) : (
                                    <Box sx={{ mt: 0.5 }}>
                                        <LocationAutocomplete
                                            value={{ ...location, description: location.description || '' }}
                                            onSelect={(address, coordinates) =>
                                                setLocation({
                                                    ...location,
                                                    address,
                                                    coordinates: coordinates || [],
                                                })
                                            }
                                            withCordinates
                                            onSelectLocationOption={() => {}}
                                            searchOptions={{ types: ['(regions)'] }}
                                            placeholder="Search region or country..."
                                            fieldStyle={{
                                                bgcolor: theme[themeStyle].gray_text,
                                                borderRadius: '8px',
                                                input: { color: theme[themeStyle].highlight },
                                                marginTop: '0px',
                                                label: { color: theme[themeStyle].tag_white },
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: 'rgba(255,255,255,0.14)',
                                                },
                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: theme[themeStyle].highlight,
                                                },
                                                '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: theme[themeStyle].highlight,
                                                },
                                            }}
                                        />
                                        {!previewMode && isEditMode && (
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    gap: sleekPerformerTechnicalActions ? 0.75 : 1.25,
                                                    mt: sleekPerformerTechnicalActions ? 1 : 1.25,
                                                }}>
                                                <Button
                                                    variant="outlined"
                                                    size={sleekPerformerTechnicalActions ? 'small' : 'medium'}
                                                    startIcon={sleekPerformerTechnicalActions ? undefined : <Cancel />}
                                                    onClick={() => {
                                                        setLocation(
                                                            profile?.location || {
                                                                address: '',
                                                                description: '',
                                                                coordinates: [],
                                                                type: '',
                                                                short_name: '',
                                                            },
                                                        );
                                                        markSectionEditing('location', false);
                                                    }}
                                                    sx={{
                                                        ...(sleekPerformerTechnicalActions
                                                            ? sleekTechCancelSx
                                                            : {
                                                                  borderColor: 'rgba(255,255,255,0.22)',
                                                                  backgroundColor: '#20201f',
                                                                  color: theme[themeStyle].primary_white,
                                                                  fontSize: '0.85rem',
                                                                  minHeight: 38,
                                                                  px: 1.75,
                                                              }),
                                                        whiteSpace: 'nowrap',
                                                        width: '100%',
                                                        '&:hover': sleekPerformerTechnicalActions
                                                            ? {
                                                                  borderColor: 'rgba(255,255,255,0.22)',
                                                                  backgroundColor: 'rgba(255, 255, 255, 0.08)',
                                                              }
                                                            : undefined,
                                                    }}>
                                                    Cancel
                                                </Button>
                                                <Button
                                                    variant="contained"
                                                    size={sleekPerformerTechnicalActions ? 'small' : 'medium'}
                                                    startIcon={
                                                        sleekPerformerTechnicalActions ? undefined : (
                                                            <CheckCircleOutline
                                                                sx={{ color: theme[themeStyle].black }}
                                                            />
                                                        )
                                                    }
                                                    onClick={() => {
                                                        onSubmit(getSectionValues('location'));
                                                        markSectionEditing('location', false);
                                                    }}
                                                    sx={{
                                                        ...(sleekPerformerTechnicalActions
                                                            ? sleekTechSaveSx
                                                            : {
                                                                  backgroundColor: theme[themeStyle].highlight,
                                                                  color: theme[themeStyle].black,
                                                                  fontSize: '0.85rem',
                                                                  minHeight: 38,
                                                                  px: 1.75,
                                                              }),
                                                        whiteSpace: 'nowrap',
                                                        width: '100%',
                                                        '&:hover': {
                                                            backgroundColor: theme[themeStyle].primary_orange,
                                                            opacity: 0.92,
                                                        },
                                                    }}>
                                                    Save
                                                </Button>
                                            </Box>
                                        )}
                                    </Box>
                                )}
                            </>
                        ) : technicalLayout ? (
                            <>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 2,
                                        flex: 1,
                                        minWidth: 0,
                                        width: editingSections.location ? '100%' : 'auto',
                                    }}>
                                    <Box
                                        sx={{
                                            width: 40,
                                            height: 40,
                                            borderRadius: '50%',
                                            bgcolor: 'rgba(250,175,63,0.12)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0,
                                        }}>
                                        <LocationOn sx={{ color: theme[themeStyle].highlight, fontSize: 22 }} />
                                    </Box>
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography
                                            sx={{
                                                fontSize: 10,
                                                fontWeight: 700,
                                                letterSpacing: '0.14em',
                                                textTransform: 'uppercase',
                                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                                color: 'rgba(255,255,255,0.45)',
                                                mb: 0.5,
                                            }}>
                                            Location
                                        </Typography>
                                        {!editingSections.location || previewMode ? (
                                            <Typography
                                                sx={{
                                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                                    color: theme[themeStyle].primary_white,
                                                    fontSize: 15,
                                                }}>
                                                {profile?.location?.address || 'No location provided'}
                                            </Typography>
                                        ) : (
                                            <Box sx={{ mt: 0.5 }}>
                                                <LocationAutocomplete
                                                    value={{ ...location, description: location.description || '' }}
                                                    onSelect={(address, coordinates) =>
                                                        setLocation({
                                                            ...location,
                                                            address,
                                                            coordinates: coordinates || [],
                                                        })
                                                    }
                                                    withCordinates
                                                    onSelectLocationOption={() => {}}
                                                    searchOptions={{ types: ['(regions)'] }}
                                                    placeholder="Search region or country..."
                                                    fieldStyle={{
                                                        bgcolor: theme[themeStyle].gray_text,
                                                        borderRadius: '8px',
                                                        input: { color: theme[themeStyle].highlight },
                                                        marginTop: '0px',
                                                        label: { color: theme[themeStyle].tag_white },
                                                        '& .MuiOutlinedInput-notchedOutline': {
                                                            borderColor: 'rgba(255,255,255,0.14)',
                                                        },
                                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                                            borderColor: theme[themeStyle].highlight,
                                                        },
                                                        '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                            borderColor: theme[themeStyle].highlight,
                                                        },
                                                    }}
                                                />
                                            </Box>
                                        )}
                                    </Box>
                                </Box>
                                {!previewMode &&
                                    isEditMode &&
                                    (editingSections.location ? (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: 1.25,
                                                flexShrink: 0,
                                                ml: 0,
                                                width: '100%',
                                                justifyContent: 'flex-start',
                                            }}>
                                            <Button
                                                variant="outlined"
                                                startIcon={<Cancel />}
                                                onClick={() => {
                                                    setLocation(
                                                        profile?.location || {
                                                            address: '',
                                                            description: '',
                                                            coordinates: [],
                                                            type: '',
                                                            short_name: '',
                                                        },
                                                    );
                                                    markSectionEditing('location', false);
                                                }}
                                                sx={{
                                                    borderColor: 'rgba(255,255,255,0.22)',
                                                    backgroundColor: '#20201f',
                                                    color: theme[themeStyle].primary_white,
                                                    fontSize: '0.85rem',
                                                    minWidth: 0,
                                                    minHeight: 38,
                                                    px: 1.75,
                                                    whiteSpace: 'nowrap',
                                                    width: '100%',
                                                }}>
                                                Cancel
                                            </Button>
                                            <Button
                                                variant="contained"
                                                startIcon={
                                                    <CheckCircleOutline sx={{ color: theme[themeStyle].black }} />
                                                }
                                                onClick={() => {
                                                    onSubmit(getSectionValues('location'));
                                                    markSectionEditing('location', false);
                                                }}
                                                sx={{
                                                    backgroundColor: theme[themeStyle].highlight,
                                                    color: theme[themeStyle].black,
                                                    fontSize: '0.85rem',
                                                    minWidth: 0,
                                                    minHeight: 38,
                                                    px: 1.75,
                                                    whiteSpace: 'nowrap',
                                                    width: '100%',
                                                }}>
                                                Save
                                            </Button>
                                        </Box>
                                    ) : (
                                        <IconButton
                                            onClick={() => markSectionEditing('location', true)}
                                            sx={{
                                                color: theme[themeStyle].primary_orange,
                                                p: 0.5,
                                                flexShrink: 0,
                                                ml: 'auto',
                                            }}>
                                            <EditRounded fontSize="small" />
                                        </IconButton>
                                    ))}
                            </>
                        ) : (
                            <>
                        <SectionHeader title="Location" section="location" />
                        {!editingSections.location || previewMode ? (
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                            color: theme[themeStyle].highlight,
                                    fontSize: 13,
                                            fontWeight: 600,
                                }}>
                                {profile?.location?.address || 'No location provided'}
                            </Typography>
                        ) : (
                            <Box sx={{ mt: 0.5 }}>
                                <LocationAutocomplete
                                    value={{ ...location, description: location.description || '' }}
                                    onSelect={(address, coordinates) =>
                                        setLocation({ ...location, address, coordinates: coordinates || [] })
                                    }
                                    withCordinates
                                    onSelectLocationOption={() => {}}
                                    searchOptions={{ types: ['(regions)'] }}
                                    placeholder="Search region or country..."
                                    fieldStyle={{
                                        bgcolor: theme[themeStyle].gray_text,
                                        borderRadius: '8px',
                                                input: { color: theme[themeStyle].highlight },
                                        marginTop: '0px',
                                        label: { color: theme[themeStyle].tag_white },
                                        '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: 'rgba(255,255,255,0.14)',
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme[themeStyle].highlight,
                                        },
                                        '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme[themeStyle].highlight,
                                        },
                                    }}
                                />
                            </Box>
                                )}
                            </>
                        )}
                    </Box>
                    <Divider sx={{ my: sidebarDividerMy, borderColor: theme[themeStyle].grey }} />
                </>
            )}

            {/* Attributes Section - Hide for Content Creators */}
            {shouldShowSection(
                profile,
                'attributes',
                'attributes',
                editingSections.attributes,
                !!(
                    profile?.gender ||
                    profile?.sex ||
                    profile?.body_type ||
                    profile?.hair_color ||
                    profile?.height ||
                    profile?.weight
                ),
            ) && (
                <>
                    <Box sx={{ mb: sidebarSectionMb }}>
                        <SectionHeader
                            title={technicalLayout ? 'Physical Attributes' : 'Attributes'}
                            section="attributes"
                            labelVariant={
                                technicalLayout ? (performerAttributesOnlyAccent ? 'orange' : 'muted') : undefined
                            }
                            suppressActionsInEdit={technicalLayout}
                        />
                        {!editingSections.attributes || previewMode ? (
                            technicalLayout ? (
                                <Grid container spacing={2}>
                                    {(
                                        [
                                            ['Gender', profile?.gender || '-'],
                                            ['Sex', profile?.sex || '-'],
                                            ['Body Type', profile?.body_type || '-'],
                                            ['Hair Color', profile?.hair_color || '-'],
                                            ['Height (cm)', profile?.height != null ? String(profile.height) : '-'],
                                            ['Weight (lbs)', profile?.weight != null ? String(profile.weight) : '-'],
                                        ] as const
                                    ).map(([label, value]) => (
                                        <Grid item xs={6} key={label}>
                                            <Box
                                                sx={{
                                                    bgcolor: ATTR_TILE_BG,
                                                    border: ATTR_TILE_BORDER,
                                                    borderRadius: '12px',
                                                    p: 2,
                                                    height: '100%',
                                                    boxSizing: 'border-box',
                                                }}>
                                                <Typography
                                                    sx={{
                                                        fontSize: 10,
                                                        fontWeight: 700,
                                                        letterSpacing: '0.12em',
                                                        textTransform: 'uppercase',
                                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                                        color: 'rgba(255,255,255,0.45)',
                                                        mb: 1,
                                                    }}>
                                                    {label}
                                                </Typography>
                                                <Typography
                                                    sx={{
                                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                                        color: theme[themeStyle].primary_white,
                                                        fontSize: 15,
                                                        lineHeight: 1.35,
                                                    }}>
                                                    {value === '-' ? (
                                                        <Box component="span" sx={{ color: 'rgba(255,255,255,0.35)' }}>
                                                            -
                                                        </Box>
                                                    ) : (
                                                        value
                                                    )}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    ))}
                                </Grid>
                            ) : (
                            <Grid container spacing={1}>
                                <Grid item xs={6}>
                                    <Typography
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            color: theme[themeStyle].light_gray_text,
                                            fontSize: 12,
                                        }}>
                                        Gender
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                                            color: theme[themeStyle].primary_white,
                                            fontSize: 13,
                                        }}>
                                        {profile?.gender || '-'}
                                    </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            color: theme[themeStyle].light_gray_text,
                                            fontSize: 12,
                                        }}>
                                        Sex
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                                            color: theme[themeStyle].primary_white,
                                            fontSize: 13,
                                        }}>
                                        {profile?.sex || '-'}
                                    </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            color: theme[themeStyle].light_gray_text,
                                            fontSize: 12,
                                        }}>
                                        Body Type
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                                            color: theme[themeStyle].primary_white,
                                            fontSize: 13,
                                        }}>
                                        {profile?.body_type || '-'}
                                    </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            color: theme[themeStyle].light_gray_text,
                                            fontSize: 12,
                                        }}>
                                        Hair Color
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                                            color: theme[themeStyle].primary_white,
                                            fontSize: 13,
                                        }}>
                                        {profile?.hair_color || '-'}
                                    </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            color: theme[themeStyle].light_gray_text,
                                            fontSize: 12,
                                        }}>
                                        Height (cm)
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                                            color: theme[themeStyle].primary_white,
                                            fontSize: 13,
                                        }}>
                                        {profile?.height || '-'}
                                    </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            color: theme[themeStyle].light_gray_text,
                                            fontSize: 12,
                                        }}>
                                        Weight (lbs)
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                                            color: theme[themeStyle].primary_white,
                                            fontSize: 13,
                                        }}>
                                        {profile?.weight || '-'}
                                    </Typography>
                                </Grid>
                            </Grid>
                            )
                        ) : (
                            <Box>
                                <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
                                    <Grid item xs={technicalLayout ? 12 : 6}>
                                    <TextField
                                        label="Gender"
                                        value={attributes.gender}
                                        onChange={e => setAttributes(a => ({ ...a, gender: e.target.value }))}
                                        fullWidth
                                            InputLabelProps={{
                                                sx: { color: 'rgba(255,255,255,0.82)' },
                                            }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                color: theme[themeStyle].tag_white,
                                                    backgroundColor: '#131313',
                                                fontSize: 13,
                                            },
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: 'rgba(255,255,255,0.14)',
                                                },
                                                '& .MuiOutlinedInput-input::placeholder': {
                                                    color: 'rgba(255,255,255,0.4)',
                                                    opacity: 1,
                                            },
                                        }}
                                    />
                                </Grid>
                                    <Grid item xs={technicalLayout ? 12 : 6}>
                                    <TextField
                                        label="Sex"
                                        value={attributes.sex}
                                        onChange={e => setAttributes(a => ({ ...a, sex: e.target.value }))}
                                        fullWidth
                                            InputLabelProps={{
                                                sx: { color: 'rgba(255,255,255,0.82)' },
                                            }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                color: theme[themeStyle].tag_white,
                                                    backgroundColor: '#131313',
                                                fontSize: 13,
                                            },
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: 'rgba(255,255,255,0.14)',
                                                },
                                                '& .MuiOutlinedInput-input::placeholder': {
                                                    color: 'rgba(255,255,255,0.4)',
                                                    opacity: 1,
                                            },
                                        }}
                                    />
                                </Grid>
                                    <Grid item xs={technicalLayout ? 12 : 6}>
                                    <TextField
                                        label="Body Type"
                                        value={attributes.body_type}
                                        onChange={e => setAttributes(a => ({ ...a, body_type: e.target.value }))}
                                        fullWidth
                                        select
                                            InputLabelProps={{
                                                sx: { color: 'rgba(255,255,255,0.82)' },
                                            }}
                                            SelectProps={{
                                                MenuProps: {
                                                    PaperProps: {
                                                        sx: {
                                                            bgcolor: TECH_SURFACE,
                                                            border: TECH_BORDER,
                                                            maxHeight: 280,
                                                            '& .MuiMenuItem-root': {
                                                                color: theme[themeStyle].primary_white,
                                                                fontSize: 14,
                                                                '&:hover': {
                                                                    bgcolor: 'rgba(255,255,255,0.08)',
                                                                },
                                                                '&.Mui-selected': {
                                                                    bgcolor: 'rgba(250,175,63,0.22)',
                                                                },
                                                            },
                                                        },
                                                    },
                                                },
                                            }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                color: theme[themeStyle].tag_white,
                                                    backgroundColor: '#131313',
                                                fontSize: 13,
                                            },
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: 'rgba(255,255,255,0.14)',
                                                },
                                                '& .MuiSelect-icon': {
                                                    color: theme[themeStyle].tag_white,
                                            },
                                        }}>
                                        {ATTR_BODY_TYPES.map(type => (
                                            <MenuItem key={type} value={type}>
                                                {type.charAt(0).toUpperCase() + type.slice(1)}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>
                                    <Grid item xs={technicalLayout ? 12 : 6}>
                                    <TextField
                                        label="Hair Color"
                                        value={attributes.hair_color}
                                        onChange={e => setAttributes(a => ({ ...a, hair_color: e.target.value }))}
                                        fullWidth
                                            InputLabelProps={{
                                                sx: { color: 'rgba(255,255,255,0.82)' },
                                            }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                color: theme[themeStyle].tag_white,
                                                    backgroundColor: '#131313',
                                                fontSize: 13,
                                            },
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: 'rgba(255,255,255,0.14)',
                                                },
                                                '& .MuiOutlinedInput-input::placeholder': {
                                                    color: 'rgba(255,255,255,0.4)',
                                                    opacity: 1,
                                            },
                                        }}
                                    />
                                </Grid>
                                    <Grid item xs={technicalLayout ? 12 : 6}>
                                    <TextField
                                        label="Height (cm)"
                                        type="number"
                                        value={attributes.height}
                                            onChange={e =>
                                                setAttributes(a => ({ ...a, height: Number(e.target.value) }))
                                            }
                                        fullWidth
                                            InputLabelProps={{
                                                sx: { color: 'rgba(255,255,255,0.82)' },
                                            }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                color: theme[themeStyle].tag_white,
                                                    backgroundColor: '#131313',
                                                fontSize: 13,
                                            },
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: 'rgba(255,255,255,0.14)',
                                            },
                                        }}
                                    />
                                </Grid>
                                    <Grid item xs={technicalLayout ? 12 : 6}>
                                    <TextField
                                        label="Weight (lbs)"
                                        type="number"
                                        value={attributes.weight}
                                            onChange={e =>
                                                setAttributes(a => ({ ...a, weight: Number(e.target.value) }))
                                            }
                                        fullWidth
                                            InputLabelProps={{
                                                sx: { color: 'rgba(255,255,255,0.82)' },
                                            }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                color: theme[themeStyle].tag_white,
                                                    backgroundColor: '#131313',
                                                fontSize: 13,
                                            },
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: 'rgba(255,255,255,0.14)',
                                            },
                                        }}
                                    />
                                </Grid>
                            </Grid>
                                {technicalLayout && renderInlineSectionActions('attributes')}
                            </Box>
                        )}
                    </Box>
                    <Divider sx={{ my: sidebarDividerMy, borderColor: theme[themeStyle].grey }} />
                </>
            )}

            {/* Skills Section */}
            {shouldShowSection(
                profile,
                'skills',
                'skills',
                editingSections.skills,
                !!(profile?.skills?.length || (mergeExpertiseSkills && profile?.other_skills?.length)),
            ) && (
                <>
                    <Box sx={{ mb: performerColumnDensity ? 1.25 : 2 }}>
                        <SectionHeader
                            title={performerCinematicLayout && mergeExpertiseSkills ? 'Expertise & Skills' : 'Skills'}
                            section="skills"
                            labelVariant={
                                technicalLayout
                                    ? performerCinematicLayout && mergeExpertiseSkills
                                        ? 'orange'
                                        : 'muted'
                                    : undefined
                            }
                            suppressActionsInEdit={technicalLayout}
                        />
                        {!editingSections.skills || previewMode ? (
                            performerCinematicLayout && mergeExpertiseSkills && technicalLayout ? (
                                <Grid container spacing={2}>
                                    {[...skills, ...otherSkills].length === 0 ? (
                                        <Grid item xs={12}>
                                            <Typography sx={{ fontSize: 13, color: theme[themeStyle].light_gray_text }}>
                                                No skills
                                            </Typography>
                                        </Grid>
                                    ) : (
                                        [...skills, ...otherSkills].map((skill, idx) => (
                                            <Grid item xs={12} md={6} key={`${skill.skill}-${idx}`}>
                                                {renderSkillChip(skill, idx, 'skills', false)}
                                            </Grid>
                                        ))
                                    )}
                                </Grid>
                            ) : (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: technicalLayout ? 'column' : 'row',
                                        alignItems: technicalLayout ? 'stretch' : 'flex-start',
                                        flexWrap: 'wrap',
                                        gap: technicalLayout ? 1.6 : 0,
                                    }}>
                                {skills.length === 0 ? (
                                    <Typography sx={{ fontSize: 13, color: theme[themeStyle].light_gray_text }}>
                                        No skills
                                    </Typography>
                                ) : (
                                    skills.map((skill, idx) =>
                                        renderSkillChip(skill, idx, 'skills', editingSections.skills),
                                    )
                                )}
                            </Box>
                            )
                        ) : (
                            <Box>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                                    {skills.map((skill, idx) =>
                                        editingSkillIndex === idx ? (
                                            <Box
                                                key={idx}
                                                sx={{
                                                    display: 'flex',
                                                    gap: 1,
                                                    mb: 1,
                                                    alignItems: technicalLayout ? 'stretch' : 'center',
                                                    flexDirection: technicalLayout ? 'column' : 'row',
                                                    width: technicalLayout ? '100%' : 'auto',
                                                }}>
                                                <Autocomplete
                                                    value={newSkill.skill}
                                                    onChange={(_, newValue) => {
                                                        if (newValue) {
                                                            setNewSkill(s => ({ ...s, skill: newValue }));
                                                        }
                                                    }}
                                                    inputValue={newSkill.skill}
                                                    onInputChange={(_, newInputValue) =>
                                                        setNewSkill(s => ({ ...s, skill: newInputValue }))
                                                    }
                                                    popupIcon={
                                                        <ArrowDropDown sx={{ color: theme[themeStyle].tag_white }} />
                                                    }
                                                    componentsProps={{
                                                        paper: {
                                                            sx: {
                                                                backgroundColor: theme[themeStyle].secondaryBg,
                                                                color: theme[themeStyle].primary_white,
                                                                '& .MuiAutocomplete-option': {
                                                                    color: theme[themeStyle].primary_white,
                                                                    '&:hover': {
                                                                        backgroundColor: theme[themeStyle].highlight,
                                                                    },
                                                                    '&.Mui-focused': {
                                                                        backgroundColor:
                                                                            theme[themeStyle].background_gradient.end,
                                                                    },
                                                                    '&.Mui-selected': {
                                                                        backgroundColor:
                                                                            theme[themeStyle].primary_orange,
                                                                        color: theme[themeStyle].black,
                                                                    },
                                                                },
                                                            },
                                                        },
                                                    }}
                                                    options={SKILLS.filter(
                                                        s => !skills.some(existing => existing.skill === s),
                                                    )}
                                                    renderInput={params => (
                                                        <TextField
                                                            {...params}
                                                            size="small"
                                                            label="Skill"
                                                            sx={{
                                                                width: technicalLayout ? '100%' : 200,
                                                                '& .MuiOutlinedInput-root': {
                                                                    backgroundColor: theme[themeStyle].gray_text,
                                                                    color: theme[themeStyle].tag_white,
                                                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                                    '& fieldset': {
                                                                        borderColor:
                                                                            theme[themeStyle].placeholderGray50,
                                                                    },
                                                                    '&:hover fieldset': {
                                                                        borderColor:
                                                                            theme[themeStyle].placeholderGray50,
                                                                    },
                                                                    '&.Mui-focused fieldset': {
                                                                        borderColor: theme[themeStyle].highlight,
                                                                    },
                                                                },
                                                                '& .MuiInputBase-input': {
                                                                    color: theme[themeStyle].tag_white,
                                                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                                },
                                                                '& .MuiInputLabel-root': {
                                                                    color: theme[themeStyle].tag_white,
                                                                },
                                                            }}
                                                        />
                                                    )}
                                                />
                                                <TextField
                                                    size="small"
                                                    select
                                                    label="Expertise"
                                                    value={newSkill.expertise}
                                                    InputLabelProps={{
                                                        sx: {
                                                            color: theme[themeStyle].tag_white,
                                                        },
                                                    }}
                                                    onChange={e =>
                                                        setNewSkill(s => ({ ...s, expertise: e.target.value as any }))
                                                    }
                                                    fullWidth
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            backgroundColor: theme[themeStyle].gray_text,
                                                            color: theme[themeStyle].tag_white,
                                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                            '& fieldset': {
                                                                borderColor: theme[themeStyle].placeholderGray50,
                                                            },
                                                            '&:hover fieldset': {
                                                                borderColor: theme[themeStyle].placeholderGray50,
                                                            },
                                                            '&.Mui-focused fieldset': {
                                                                borderColor: theme[themeStyle].highlight,
                                                            },
                                                        },
                                                        '& .MuiInputBase-input': {
                                                            color: theme[themeStyle].tag_white,
                                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                        },
                                                    }}>
                                                    <MenuItem value="beginner">Beginner</MenuItem>
                                                    <MenuItem value="mid-level">Mid-level</MenuItem>
                                                    <MenuItem value="expert">Expert</MenuItem>
                                                </TextField>
                                                <TextField
                                                    size="small"
                                                    type="number"
                                                    label="Years of Experience"
                                                    value={newSkill.years_of_experience}
                                                    InputLabelProps={{
                                                        sx: {
                                                            color: theme[themeStyle].tag_white,
                                                        },
                                                    }}
                                                    onChange={e =>
                                                        setNewSkill(s => ({
                                                            ...s,
                                                            years_of_experience: Number(e.target.value),
                                                        }))
                                                    }
                                                    fullWidth
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            backgroundColor: theme[themeStyle].gray_text,
                                                            color: theme[themeStyle].tag_white,
                                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                            '& fieldset': {
                                                                borderColor: theme[themeStyle].placeholderGray50,
                                                            },
                                                            '&:hover fieldset': {
                                                                borderColor: theme[themeStyle].placeholderGray50,
                                                            },
                                                            '&.Mui-focused fieldset': {
                                                                borderColor: theme[themeStyle].highlight,
                                                            },
                                                        },
                                                        '& .MuiInputBase-input': {
                                                            color: theme[themeStyle].tag_white,
                                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                        },
                                                    }}
                                                />
                                                <TextField
                                                    size="small"
                                                    label="Description"
                                                    value={newSkill.description}
                                                    InputLabelProps={{
                                                        sx: {
                                                            color: theme[themeStyle].tag_white,
                                                        },
                                                    }}
                                                    onChange={e =>
                                                        setNewSkill(s => ({ ...s, description: e.target.value }))
                                                    }
                                                    fullWidth
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            backgroundColor: theme[themeStyle].gray_text,
                                                            color: theme[themeStyle].tag_white,
                                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                            '& fieldset': {
                                                                borderColor: theme[themeStyle].placeholderGray50,
                                                            },
                                                            '&:hover fieldset': {
                                                                borderColor: theme[themeStyle].placeholderGray50,
                                                            },
                                                            '&.Mui-focused fieldset': {
                                                                borderColor: theme[themeStyle].highlight,
                                                            },
                                                        },
                                                        '& .MuiInputBase-input': {
                                                            color: theme[themeStyle].tag_white,
                                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                        },
                                                    }}
                                                />
                                                <Button
                                                    size="small"
                                                    variant="contained"
                                                    onClick={() => {
                                                        setSkills(skills.map((s, i) => (i === idx ? newSkill : s)));
                                                        setEditingSkillIndex(null);
                                                    }}>
                                                    Save
                                                </Button>
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    onClick={() => setEditingSkillIndex(null)}>
                                                    Cancel
                                                </Button>
                                            </Box>
                                        ) : (
                                            renderSkillChip(skill, idx, 'skills', editingSections.skills)
                                        ),
                                    )}
                                </Box>
                                {!showAddSkillForm ? (
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<AddCircleOutline />}
                                        onClick={() => setShowAddSkillForm(true)}
                                        sx={{
                                            borderColor: theme[themeStyle].primary_orange,
                                            color: theme[themeStyle].primary_white,
                                            '&:hover': {
                                                borderColor: theme[themeStyle].primary_orange,
                                                backgroundColor: 'rgba(255, 140, 0, 0.1)',
                                            },
                                        }}>
                                        Add New Skill
                                    </Button>
                                ) : (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 1,
                                            mt: 1,
                                            p: 2,
                                            bgcolor: theme[themeStyle].gray_text,
                                            borderRadius: 1,
                                        }}>
                                        <Typography
                                            sx={{ fontSize: 14, color: theme[themeStyle].primary_white, mb: 1 }}>
                                            Add New Skill
                                        </Typography>
                                        <Grid container spacing={1}>
                                            <Grid item xs={12} sm={6}>
                                                <Autocomplete
                                                    value={newSkill.skill}
                                                    onChange={(_, newValue) => {
                                                        if (newValue) {
                                                            setNewSkill(s => ({ ...s, skill: newValue }));
                                                        }
                                                    }}
                                                    inputValue={newSkill.skill}
                                                    onInputChange={(_, newInputValue) =>
                                                        setNewSkill(s => ({ ...s, skill: newInputValue }))
                                                    }
                                                    options={SKILLS.filter(
                                                        s => !skills.some(existing => existing.skill === s),
                                                    )}
                                                    popupIcon={
                                                        <ArrowDropDown sx={{ color: theme[themeStyle].tag_white }} />
                                                    }
                                                    componentsProps={{
                                                        paper: {
                                                            sx: {
                                                                backgroundColor: theme[themeStyle].secondaryBg,
                                                                color: theme[themeStyle].primary_white,
                                                                '& .MuiAutocomplete-option': {
                                                                    color: theme[themeStyle].primary_white,
                                                                    '&:hover': {
                                                                        backgroundColor: theme[themeStyle].highlight,
                                                                    },
                                                                    '&.Mui-focused': {
                                                                        backgroundColor:
                                                                            theme[themeStyle].background_gradient.end,
                                                                    },
                                                                    '&.Mui-selected': {
                                                                        backgroundColor:
                                                                            theme[themeStyle].primary_orange,
                                                                        color: theme[themeStyle].black,
                                                                    },
                                                                },
                                                            },
                                                        },
                                                    }}
                                                    renderInput={params => (
                                                        <TextField
                                                            {...params}
                                                            size="small"
                                                            label="Skill"
                                                            fullWidth
                                                            sx={{
                                                                '& .MuiOutlinedInput-root': {
                                                                    backgroundColor: theme[themeStyle].gray_text,
                                                                    color: theme[themeStyle].tag_white,
                                                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                                    '& fieldset': {
                                                                        borderColor:
                                                                            theme[themeStyle].placeholderGray50,
                                                                    },
                                                                    '&:hover fieldset': {
                                                                        borderColor:
                                                                            theme[themeStyle].placeholderGray50,
                                                                    },
                                                                    '&.Mui-focused fieldset': {
                                                                        borderColor: theme[themeStyle].highlight,
                                                                    },
                                                                },
                                                                '& .MuiInputBase-input': {
                                                                    color: theme[themeStyle].tag_white,
                                                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                                },
                                                                '& .MuiInputLabel-root': {
                                                                    color: theme[themeStyle].tag_white,
                                                                },
                                                            }}
                                                        />
                                                    )}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    size="small"
                                                    select
                                                    label="Expertise"
                                                    value={newSkill.expertise}
                                                    onChange={e =>
                                                        setNewSkill(s => ({ ...s, expertise: e.target.value as any }))
                                                    }
                                                    fullWidth
                                                    SelectProps={{
                                                        MenuProps: {
                                                            PaperProps: {
                                                                sx: {
                                                                    backgroundColor: theme[themeStyle].secondaryBg,
                                                                    color: theme[themeStyle].primary_white,
                                                                    '& .MuiMenuItem-root': {
                                                                        color: theme[themeStyle].primary_white,
                                                                        '&:hover': {
                                                                            backgroundColor:
                                                                                theme[themeStyle].highlight,
                                                                        },
                                                                        '&.Mui-selected': {
                                                                            backgroundColor:
                                                                                theme[themeStyle].primary_orange,
                                                                            color: theme[themeStyle].black,
                                                                            '&:hover': {
                                                                                backgroundColor:
                                                                                    theme[themeStyle].primary_orange,
                                                                            },
                                                                        },
                                                                    },
                                                                },
                                                            },
                                                        },
                                                    }}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            backgroundColor: theme[themeStyle].gray_text,
                                                            color: theme[themeStyle].tag_white,
                                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                            '& fieldset': {
                                                                borderColor: theme[themeStyle].placeholderGray50,
                                                            },
                                                            '&:hover fieldset': {
                                                                borderColor: theme[themeStyle].placeholderGray50,
                                                            },
                                                            '&.Mui-focused fieldset': {
                                                                borderColor: theme[themeStyle].highlight,
                                                            },
                                                        },
                                                        '& .MuiInputBase-input': {
                                                            color: theme[themeStyle].tag_white,
                                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                        },
                                                        '& .MuiInputLabel-root': {
                                                            color: theme[themeStyle].tag_white,
                                                        },
                                                    }}>
                                                    <MenuItem value="beginner">Beginner</MenuItem>
                                                    <MenuItem value="mid-level">Mid-level</MenuItem>
                                                    <MenuItem value="expert">Expert</MenuItem>
                                                </TextField>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    size="small"
                                                    type="number"
                                                    label="Years of Experience"
                                                    value={newSkill.years_of_experience}
                                                    onChange={e =>
                                                        setNewSkill(s => ({
                                                            ...s,
                                                            years_of_experience: Number(e.target.value),
                                                        }))
                                                    }
                                                    fullWidth
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            backgroundColor: theme[themeStyle].gray_text,
                                                            color: theme[themeStyle].tag_white,
                                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                            '& fieldset': {
                                                                borderColor: theme[themeStyle].placeholderGray50,
                                                            },
                                                            '&:hover fieldset': {
                                                                borderColor: theme[themeStyle].placeholderGray50,
                                                            },
                                                            '&.Mui-focused fieldset': {
                                                                borderColor: theme[themeStyle].highlight,
                                                            },
                                                        },
                                                        '& .MuiInputBase-input': {
                                                            color: theme[themeStyle].tag_white,
                                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                        },
                                                        '& .MuiInputLabel-root': {
                                                            color: theme[themeStyle].tag_white,
                                                        },
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    size="small"
                                                    label="Description"
                                                    value={newSkill.description}
                                                    onChange={e =>
                                                        setNewSkill(s => ({ ...s, description: e.target.value }))
                                                    }
                                                    fullWidth
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            backgroundColor: theme[themeStyle].gray_text,
                                                            color: theme[themeStyle].tag_white,
                                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                            '& fieldset': {
                                                                borderColor: theme[themeStyle].placeholderGray50,
                                                            },
                                                            '&:hover fieldset': {
                                                                borderColor: theme[themeStyle].placeholderGray50,
                                                            },
                                                            '&.Mui-focused fieldset': {
                                                                borderColor: theme[themeStyle].highlight,
                                                            },
                                                        },
                                                        '& .MuiInputBase-input': {
                                                            color: theme[themeStyle].tag_white,
                                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                        },
                                                        '& .MuiInputLabel-root': {
                                                            color: theme[themeStyle].tag_white,
                                                        },
                                                    }}
                                                />
                                            </Grid>
                                        </Grid>
                                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 1 }}>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                onClick={() => {
                                                    setShowAddSkillForm(false);
                                                    setNewSkill({
                                                        skill: '',
                                                        expertise: 'beginner',
                                                        years_of_experience: 1,
                                                        description: '',
                                                    });
                                                }}>
                                                Cancel
                                            </Button>
                                            <Button
                                                size="small"
                                                variant="contained"
                                                onClick={() => {
                                                    if (newSkill.skill) {
                                                        setSkills([...skills, newSkill]);
                                                        setNewSkill({
                                                            skill: '',
                                                            expertise: 'beginner',
                                                            years_of_experience: 1,
                                                            description: '',
                                                        });
                                                        setShowAddSkillForm(false);
                                                    }
                                                }}>
                                                Add Skill
                                            </Button>
                                        </Box>
                                    </Box>
                                )}
                            </Box>
                        )}
                        {renderInlineSectionActions('skills')}
                    </Box>
                    <Divider sx={{ my: performerColumnDensity ? 1 : 2, borderColor: theme[themeStyle].grey }} />
                </>
            )}

            {/* Other Skills Section */}
            {!mergeExpertiseSkills &&
                shouldShowSection(
                profile,
                'other_skills',
                'other_skills',
                editingSections.other_skills,
                !!profile?.other_skills,
            ) && (
                <>
                    <Box sx={{ mb: 2 }}>
                            <SectionHeader
                                title="Other Skills"
                                section="other_skills"
                                suppressActionsInEdit={technicalLayout}
                            />
                        {!editingSections.other_skills || previewMode ? (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'row',
                                        alignItems: 'flex-start',
                                        flexWrap: 'wrap',
                                        gap: technicalLayout ? 1 : 0,
                                    }}>
                                {otherSkills.length === 0 ? (
                                    <Typography sx={{ fontSize: 13, color: theme[themeStyle].light_gray_text }}>
                                        No other skills
                                    </Typography>
                                ) : (
                                    otherSkills.map((skill, idx) =>
                                        renderSkillChip(skill, idx, 'other_skills', editingSections.other_skills),
                                    )
                                )}
                            </Box>
                        ) : (
                            <Box>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                                    {otherSkills.map((skill, idx) => (
                                        <Box key={idx}>
                                            {editingSkillIndex === idx ? (
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            gap: 1,
                                                            mb: 1,
                                                            alignItems: technicalLayout ? 'stretch' : 'center',
                                                            flexDirection: technicalLayout ? 'column' : 'row',
                                                            width: technicalLayout ? '100%' : 'auto',
                                                        }}>
                                                    <Autocomplete
                                                        value={newSkill.skill}
                                                        onChange={(_, newValue) => {
                                                            if (newValue) {
                                                                setNewSkill(s => ({ ...s, skill: newValue }));
                                                            }
                                                        }}
                                                        inputValue={newSkill.skill}
                                                        onInputChange={(_, newInputValue) =>
                                                            setNewSkill(s => ({ ...s, skill: newInputValue }))
                                                        }
                                                        options={SKILLS.filter(
                                                                s =>
                                                                    !otherSkills.some(existing => existing.skill === s),
                                                        )}
                                                        popupIcon={
                                                            <ArrowDropDown
                                                                sx={{ color: theme[themeStyle].tag_white }}
                                                            />
                                                        }
                                                        componentsProps={{
                                                            paper: {
                                                                sx: {
                                                                    backgroundColor: theme[themeStyle].secondaryBg,
                                                                    color: theme[themeStyle].primary_white,
                                                                    '& .MuiAutocomplete-option': {
                                                                        color: theme[themeStyle].primary_white,
                                                                        '&:hover': {
                                                                            backgroundColor:
                                                                                theme[themeStyle].highlight,
                                                                        },
                                                                        '&.Mui-focused': {
                                                                            backgroundColor:
                                                                                    theme[themeStyle]
                                                                                        .background_gradient.end,
                                                                        },
                                                                        '&.Mui-selected': {
                                                                            backgroundColor:
                                                                                theme[themeStyle].primary_orange,
                                                                            color: theme[themeStyle].black,
                                                                        },
                                                                    },
                                                                },
                                                            },
                                                        }}
                                                        renderInput={params => (
                                                            <TextField
                                                                {...params}
                                                                size="small"
                                                                label="Skill"
                                                                fullWidth
                                                                sx={{
                                                                    '& .MuiOutlinedInput-root': {
                                                                            backgroundColor:
                                                                                theme[themeStyle].gray_text,
                                                                        color: theme[themeStyle].tag_white,
                                                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                                        '& fieldset': {
                                                                            borderColor:
                                                                                theme[themeStyle].placeholderGray50,
                                                                        },
                                                                        '&:hover fieldset': {
                                                                            borderColor:
                                                                                theme[themeStyle].placeholderGray50,
                                                                        },
                                                                        '&.Mui-focused fieldset': {
                                                                                borderColor:
                                                                                    theme[themeStyle].highlight,
                                                                        },
                                                                    },
                                                                    '& .MuiInputBase-input': {
                                                                        color: theme[themeStyle].tag_white,
                                                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                                    },
                                                                    '& .MuiInputLabel-root': {
                                                                        color: theme[themeStyle].tag_white,
                                                                    },
                                                                }}
                                                            />
                                                        )}
                                                    />
                                                    <TextField
                                                        size="small"
                                                        select
                                                        label="Expertise"
                                                        value={newSkill.expertise}
                                                        onChange={e =>
                                                            setNewSkill(s => ({
                                                                ...s,
                                                                expertise: e.target.value as any,
                                                            }))
                                                        }
                                                        fullWidth
                                                        SelectProps={{
                                                            MenuProps: {
                                                                PaperProps: {
                                                                    sx: {
                                                                            backgroundColor:
                                                                                theme[themeStyle].secondaryBg,
                                                                        color: theme[themeStyle].primary_white,
                                                                        '& .MuiMenuItem-root': {
                                                                            color: theme[themeStyle].primary_white,
                                                                            '&:hover': {
                                                                                backgroundColor:
                                                                                    theme[themeStyle].highlight,
                                                                            },
                                                                            '&.Mui-selected': {
                                                                                backgroundColor:
                                                                                        theme[themeStyle]
                                                                                            .primary_orange,
                                                                                color: theme[themeStyle].black,
                                                                                '&:hover': {
                                                                                    backgroundColor:
                                                                                        theme[themeStyle]
                                                                                            .primary_orange,
                                                                                },
                                                                            },
                                                                        },
                                                                    },
                                                                },
                                                            },
                                                        }}
                                                        sx={{
                                                            '& .MuiOutlinedInput-root': {
                                                                backgroundColor: theme[themeStyle].gray_text,
                                                                color: theme[themeStyle].tag_white,
                                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                                '& fieldset': {
                                                                        borderColor:
                                                                            theme[themeStyle].placeholderGray50,
                                                                },
                                                                '&:hover fieldset': {
                                                                        borderColor:
                                                                            theme[themeStyle].placeholderGray50,
                                                                },
                                                                '&.Mui-focused fieldset': {
                                                                    borderColor: theme[themeStyle].highlight,
                                                                },
                                                            },
                                                            '& .MuiInputBase-input': {
                                                                color: theme[themeStyle].tag_white,
                                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                            },
                                                            '& .MuiInputLabel-root': {
                                                                color: theme[themeStyle].tag_white,
                                                            },
                                                        }}>
                                                        <MenuItem value="beginner">Beginner</MenuItem>
                                                        <MenuItem value="mid-level">Mid-level</MenuItem>
                                                        <MenuItem value="expert">Expert</MenuItem>
                                                    </TextField>
                                                    <TextField
                                                        size="small"
                                                        type="number"
                                                        label="Years of Experience"
                                                        value={newSkill.years_of_experience}
                                                        onChange={e =>
                                                            setNewSkill(s => ({
                                                                ...s,
                                                                years_of_experience: Number(e.target.value),
                                                            }))
                                                        }
                                                        fullWidth
                                                        sx={{
                                                            '& .MuiOutlinedInput-root': {
                                                                backgroundColor: theme[themeStyle].gray_text,
                                                                color: theme[themeStyle].tag_white,
                                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                                '& fieldset': {
                                                                        borderColor:
                                                                            theme[themeStyle].placeholderGray50,
                                                                },
                                                                '&:hover fieldset': {
                                                                        borderColor:
                                                                            theme[themeStyle].placeholderGray50,
                                                                },
                                                                '&.Mui-focused fieldset': {
                                                                    borderColor: theme[themeStyle].highlight,
                                                                },
                                                            },
                                                            '& .MuiInputBase-input': {
                                                                color: theme[themeStyle].tag_white,
                                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                            },
                                                            '& .MuiInputLabel-root': {
                                                                color: theme[themeStyle].tag_white,
                                                            },
                                                        }}
                                                    />
                                                    <TextField
                                                        size="small"
                                                        label="Description"
                                                        value={newSkill.description}
                                                        onChange={e =>
                                                                setNewSkill(s => ({
                                                                    ...s,
                                                                    description: e.target.value,
                                                                }))
                                                        }
                                                        fullWidth
                                                        sx={{
                                                            '& .MuiOutlinedInput-root': {
                                                                backgroundColor: theme[themeStyle].gray_text,
                                                                color: theme[themeStyle].tag_white,
                                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                                '& fieldset': {
                                                                        borderColor:
                                                                            theme[themeStyle].placeholderGray50,
                                                                },
                                                                '&:hover fieldset': {
                                                                        borderColor:
                                                                            theme[themeStyle].placeholderGray50,
                                                                },
                                                                '&.Mui-focused fieldset': {
                                                                    borderColor: theme[themeStyle].highlight,
                                                                },
                                                            },
                                                            '& .MuiInputBase-input': {
                                                                color: theme[themeStyle].tag_white,
                                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                            },
                                                            '& .MuiInputLabel-root': {
                                                                color: theme[themeStyle].tag_white,
                                                            },
                                                        }}
                                                    />
                                                    <Button
                                                        size="small"
                                                        variant="contained"
                                                        onClick={() => {
                                                            setOtherSkills(
                                                                    otherSkills.map((s, i) =>
                                                                        i === idx ? newSkill : s,
                                                                    ),
                                                            );
                                                            onSubmit({
                                                                other_skills: otherSkills.map((s, i) =>
                                                                    i === idx ? newSkill : s,
                                                                ),
                                                            });
                                                            setEditingSkillIndex(null);
                                                        }}>
                                                        Save
                                                    </Button>
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        onClick={() => {
                                                            setEditingSkillIndex(null);
                                                            setNewSkill({
                                                                skill: '',
                                                                expertise: 'beginner',
                                                                years_of_experience: 1,
                                                                description: '',
                                                            });
                                                        }}>
                                                        Cancel
                                                    </Button>
                                                </Box>
                                            ) : (
                                                renderSkillChip(
                                                    skill,
                                                    idx,
                                                    'other_skills',
                                                    editingSections.other_skills,
                                                )
                                            )}
                                        </Box>
                                    ))}
                                </Box>
                                {!showAddOtherSkillForm ? (
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<AddCircleOutline />}
                                        onClick={() => setShowAddOtherSkillForm(true)}
                                        sx={{
                                            borderColor: theme[themeStyle].primary_orange,
                                            color: theme[themeStyle].primary_white,
                                            '&:hover': {
                                                borderColor: theme[themeStyle].primary_orange,
                                                backgroundColor: 'rgba(255, 140, 0, 0.1)',
                                            },
                                        }}>
                                        Add New Other Skill
                                    </Button>
                                ) : (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 1,
                                            mt: 1,
                                            p: 2,
                                            bgcolor: theme[themeStyle].gray_text,
                                            borderRadius: 1,
                                        }}>
                                        <Typography
                                            sx={{ fontSize: 14, color: theme[themeStyle].primary_white, mb: 1 }}>
                                            Add New Other Skill
                                        </Typography>
                                        <Grid container spacing={1}>
                                            <Grid item xs={12} sm={6}>
                                                <Autocomplete
                                                    value={newSkill.skill}
                                                    onChange={(_, newValue) => {
                                                        if (newValue) {
                                                            setNewSkill(s => ({ ...s, skill: newValue }));
                                                        }
                                                    }}
                                                    inputValue={newSkill.skill}
                                                    onInputChange={(_, newInputValue) =>
                                                        setNewSkill(s => ({ ...s, skill: newInputValue }))
                                                    }
                                                    options={SKILLS.filter(
                                                        s => !otherSkills.some(existing => existing.skill === s),
                                                    )}
                                                    popupIcon={
                                                            <ArrowDropDown
                                                                sx={{ color: theme[themeStyle].tag_white }}
                                                            />
                                                    }
                                                    componentsProps={{
                                                        paper: {
                                                            sx: {
                                                                backgroundColor: theme[themeStyle].secondaryBg,
                                                                color: theme[themeStyle].primary_white,
                                                                '& .MuiAutocomplete-option': {
                                                                    color: theme[themeStyle].primary_white,
                                                                    '&:hover': {
                                                                            backgroundColor:
                                                                                theme[themeStyle].highlight,
                                                                    },
                                                                    '&.Mui-focused': {
                                                                        backgroundColor:
                                                                                theme[themeStyle].background_gradient
                                                                                    .end,
                                                                    },
                                                                    '&.Mui-selected': {
                                                                        backgroundColor:
                                                                            theme[themeStyle].primary_orange,
                                                                        color: theme[themeStyle].black,
                                                                    },
                                                                },
                                                            },
                                                        },
                                                    }}
                                                    renderInput={params => (
                                                        <TextField
                                                            {...params}
                                                            size="small"
                                                            label="Skill"
                                                            fullWidth
                                                            sx={{
                                                                '& .MuiOutlinedInput-root': {
                                                                    backgroundColor: theme[themeStyle].gray_text,
                                                                    color: theme[themeStyle].tag_white,
                                                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                                    '& fieldset': {
                                                                        borderColor:
                                                                            theme[themeStyle].placeholderGray50,
                                                                    },
                                                                    '&:hover fieldset': {
                                                                        borderColor:
                                                                            theme[themeStyle].placeholderGray50,
                                                                    },
                                                                    '&.Mui-focused fieldset': {
                                                                        borderColor: theme[themeStyle].highlight,
                                                                    },
                                                                },
                                                                '& .MuiInputBase-input': {
                                                                    color: theme[themeStyle].tag_white,
                                                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                                },
                                                                '& .MuiInputLabel-root': {
                                                                    color: theme[themeStyle].tag_white,
                                                                },
                                                            }}
                                                        />
                                                    )}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    size="small"
                                                    select
                                                    label="Expertise"
                                                    value={newSkill.expertise}
                                                    onChange={e =>
                                                            setNewSkill(s => ({
                                                                ...s,
                                                                expertise: e.target.value as any,
                                                            }))
                                                    }
                                                    fullWidth
                                                    SelectProps={{
                                                        MenuProps: {
                                                            PaperProps: {
                                                                sx: {
                                                                    backgroundColor: theme[themeStyle].secondaryBg,
                                                                    color: theme[themeStyle].primary_white,
                                                                    '& .MuiMenuItem-root': {
                                                                        color: theme[themeStyle].primary_white,
                                                                        '&:hover': {
                                                                            backgroundColor:
                                                                                theme[themeStyle].highlight,
                                                                        },
                                                                        '&.Mui-selected': {
                                                                            backgroundColor:
                                                                                theme[themeStyle].primary_orange,
                                                                            color: theme[themeStyle].black,
                                                                            '&:hover': {
                                                                                backgroundColor:
                                                                                        theme[themeStyle]
                                                                                            .primary_orange,
                                                                            },
                                                                        },
                                                                    },
                                                                },
                                                            },
                                                        },
                                                    }}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            backgroundColor: theme[themeStyle].gray_text,
                                                            color: theme[themeStyle].tag_white,
                                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                            '& fieldset': {
                                                                borderColor: theme[themeStyle].placeholderGray50,
                                                            },
                                                            '&:hover fieldset': {
                                                                borderColor: theme[themeStyle].placeholderGray50,
                                                            },
                                                            '&.Mui-focused fieldset': {
                                                                borderColor: theme[themeStyle].highlight,
                                                            },
                                                        },
                                                        '& .MuiInputBase-input': {
                                                            color: theme[themeStyle].tag_white,
                                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                        },
                                                        '& .MuiInputLabel-root': {
                                                            color: theme[themeStyle].tag_white,
                                                        },
                                                    }}>
                                                    <MenuItem value="beginner">Beginner</MenuItem>
                                                    <MenuItem value="mid-level">Mid-level</MenuItem>
                                                    <MenuItem value="expert">Expert</MenuItem>
                                                </TextField>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    size="small"
                                                    type="number"
                                                    label="Years of Experience"
                                                    value={newSkill.years_of_experience}
                                                    onChange={e =>
                                                        setNewSkill(s => ({
                                                            ...s,
                                                            years_of_experience: Number(e.target.value),
                                                        }))
                                                    }
                                                    fullWidth
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            backgroundColor: theme[themeStyle].gray_text,
                                                            color: theme[themeStyle].tag_white,
                                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                            '& fieldset': {
                                                                borderColor: theme[themeStyle].placeholderGray50,
                                                            },
                                                            '&:hover fieldset': {
                                                                borderColor: theme[themeStyle].placeholderGray50,
                                                            },
                                                            '&.Mui-focused fieldset': {
                                                                borderColor: theme[themeStyle].highlight,
                                                            },
                                                        },
                                                        '& .MuiInputBase-input': {
                                                            color: theme[themeStyle].tag_white,
                                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                        },
                                                        '& .MuiInputLabel-root': {
                                                            color: theme[themeStyle].tag_white,
                                                        },
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    size="small"
                                                    label="Description"
                                                    value={newSkill.description}
                                                    onChange={e =>
                                                        setNewSkill(s => ({ ...s, description: e.target.value }))
                                                    }
                                                    fullWidth
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            backgroundColor: theme[themeStyle].gray_text,
                                                            color: theme[themeStyle].tag_white,
                                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                            '& fieldset': {
                                                                borderColor: theme[themeStyle].placeholderGray50,
                                                            },
                                                            '&:hover fieldset': {
                                                                borderColor: theme[themeStyle].placeholderGray50,
                                                            },
                                                            '&.Mui-focused fieldset': {
                                                                borderColor: theme[themeStyle].highlight,
                                                            },
                                                        },
                                                        '& .MuiInputBase-input': {
                                                            color: theme[themeStyle].tag_white,
                                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                        },
                                                        '& .MuiInputLabel-root': {
                                                            color: theme[themeStyle].tag_white,
                                                        },
                                                    }}
                                                />
                                            </Grid>
                                        </Grid>
                                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 1 }}>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                onClick={() => {
                                                    setShowAddOtherSkillForm(false);
                                                    setNewSkill({
                                                        skill: '',
                                                        expertise: 'beginner',
                                                        years_of_experience: 1,
                                                        description: '',
                                                    });
                                                }}>
                                                Cancel
                                            </Button>
                                            <Button
                                                size="small"
                                                variant="contained"
                                                onClick={() => {
                                                    if (newSkill.skill) {
                                                        setOtherSkills([...otherSkills, newSkill]);
                                                        setNewSkill({
                                                            skill: '',
                                                            expertise: 'beginner',
                                                            years_of_experience: 1,
                                                            description: '',
                                                        });
                                                        setShowAddOtherSkillForm(false);
                                                    }
                                                }}>
                                                Add Skill
                                            </Button>
                                        </Box>
                                    </Box>
                                )}
                            </Box>
                        )}
                            {renderInlineSectionActions('other_skills')}
                    </Box>
                    <Divider sx={{ my: 2, borderColor: theme[themeStyle].grey }} />
                </>
            )}

            {/* Languages Section */}
            {shouldShowSection(
                profile,
                'language_spoken',
                'language_spoken',
                editingSections.languages,
                !!profile?.language_spoken,
            ) &&
                !(artistMergedAboutMeta && !editingSections.languages) && (
                <>
                    <Box sx={{ mb: 2 }}>
                            <SectionHeader
                                title="Languages"
                                section="languages"
                                suppressActionsInEdit={technicalLayout}
                            />
                        {!editingSections.languages || previewMode ? (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
                                {languages.length === 0 ? (
                                    <Typography sx={{ fontSize: 13, color: theme[themeStyle].light_gray_text }}>
                                        No languages
                                    </Typography>
                                    ) : technicalLayout ? (
                                        <Typography
                                            sx={{
                                                fontSize: 14,
                                                color: theme[themeStyle].primary_white,
                                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                                textTransform: 'capitalize',
                                            }}>
                                            {languages[0]}
                                    </Typography>
                                ) : (
                                    languages.map((lang, idx) => (
                                        <Box
                                            key={idx}
                                            sx={{
                                                    bgcolor: '#20201f',
                                                    borderRadius: '999px',
                                                    px: 1.5,
                                                    py: 0.75,
                                                mr: 1,
                                                mb: 1,
                                                    border: '1px solid rgba(255,255,255,0.04)',
                                            }}>
                                            <Typography
                                                sx={{
                                                    fontSize: 12,
                                                        color: theme[themeStyle].highlight,
                                                    textTransform: 'capitalize',
                                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                                }}>
                                                {lang}
                                            </Typography>
                                        </Box>
                                    ))
                                )}
                            </Box>
                        ) : (
                            <Box>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                                    {languages.map((lang, idx) => (
                                        <Box
                                            key={idx}
                                            sx={{
                                                    bgcolor: '#20201f',
                                                    borderRadius: '999px',
                                                    px: 1.5,
                                                    py: 0.75,
                                                mr: 1,
                                                mb: 1,
                                                display: 'flex',
                                                alignItems: 'center',
                                                    border: '1px solid rgba(255,255,255,0.04)',
                                            }}>
                                            <Typography
                                                sx={{
                                                    fontSize: 12,
                                                        color: theme[themeStyle].highlight,
                                                    textTransform: 'capitalize',
                                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                                }}>
                                                {lang}
                                            </Typography>
                                            {editingSections.languages && (
                                                <IconButton
                                                    size="small"
                                                    sx={{ ml: 0.2, p: 0.2, color: theme[themeStyle].highlight }}
                                                    onClick={() => {
                                                        const newLanguages = languages.filter((_, i) => i !== idx);
                                                        setLanguages(newLanguages);
                                                        onSubmit({ language_spoken: newLanguages });
                                                    }}>
                                                    <Cancel fontSize="inherit" />
                                                </IconButton>
                                            )}
                                        </Box>
                                    ))}
                                </Box>
                                <Box sx={{ mt: 1 }}>
                                    <Autocomplete
                                        value={newLanguage}
                                        onChange={(_, newValue) => {
                                            if (newValue && !languages.includes(newValue)) {
                                                setLanguages([...languages, newValue]);
                                            }
                                        }}
                                        inputValue={newLanguage}
                                        onInputChange={(_, newInputValue) => setNewLanguage(newInputValue)}
                                        options={LANGUAGES.filter(lang => !languages.includes(lang))}
                                        popupIcon={<ArrowDropDown sx={{ color: theme[themeStyle].tag_white }} />}
                                        componentsProps={{
                                            paper: {
                                                sx: {
                                                    backgroundColor: theme[themeStyle].secondaryBg,
                                                    color: theme[themeStyle].primary_white,
                                                    '& .MuiAutocomplete-option': {
                                                        color: theme[themeStyle].primary_white,
                                                        '&:hover': {
                                                            backgroundColor: theme[themeStyle].highlight,
                                                        },
                                                        '&.Mui-focused': {
                                                                backgroundColor:
                                                                    theme[themeStyle].background_gradient.end,
                                                        },
                                                        '&.Mui-selected': {
                                                            backgroundColor: theme[themeStyle].primary_orange,
                                                            color: theme[themeStyle].black,
                                                        },
                                                    },
                                                },
                                            },
                                        }}
                                        renderInput={params => (
                                            <TextField
                                                {...params}
                                                size="small"
                                                label="Add Language"
                                                fullWidth
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        backgroundColor: theme[themeStyle].gray_text,
                                                        color: theme[themeStyle].tag_white,
                                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                        '& fieldset': {
                                                            borderColor: theme[themeStyle].placeholderGray50,
                                                        },
                                                        '&:hover fieldset': {
                                                            borderColor: theme[themeStyle].placeholderGray50,
                                                        },
                                                        '&.Mui-focused fieldset': {
                                                            borderColor: theme[themeStyle].highlight,
                                                        },
                                                    },
                                                    '& .MuiInputBase-input': {
                                                        color: theme[themeStyle].tag_white,
                                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                    },
                                                    '& .MuiInputLabel-root': {
                                                        color: theme[themeStyle].tag_white,
                                                    },
                                                }}
                                            />
                                        )}
                                    />
                                </Box>
                            </Box>
                        )}
                            {renderInlineSectionActions('languages')}
                    </Box>
                    <Divider sx={{ my: 2, borderColor: theme[themeStyle].grey }} />
                </>
            )}

            {/* Ethnicity Section */}
            {shouldShowSection(profile, 'ethnicity', 'ethnicity', editingSections.ethnicity, !!profile?.ethnicity) &&
                !(artistMergedAboutMeta && !editingSections.ethnicity) && (
                <>
                    <Box sx={{ mb: 2 }}>
                            <SectionHeader
                                title="Ethnicity"
                                section="ethnicity"
                                suppressActionsInEdit={technicalLayout}
                            />
                        {!editingSections.ethnicity || previewMode ? (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
                                {ethnicity.length === 0 ? (
                                    <Typography sx={{ fontSize: 13, color: theme[themeStyle].light_gray_text }}>
                                        No ethnicity
                                    </Typography>
                                ) : (
                                    ethnicity.map((eth, idx) => (
                                        <Box
                                            key={idx}
                                            sx={{
                                                bgcolor: theme[themeStyle].gray_text,
                                                borderRadius: 1,
                                                px: 1,
                                                py: 0.5,
                                                mr: 1,
                                                mb: 1,
                                            }}>
                                                <Typography
                                                    sx={{ fontSize: 12, color: theme[themeStyle].primary_white }}>
                                                {eth}
                                            </Typography>
                                        </Box>
                                    ))
                                )}
                            </Box>
                        ) : (
                            <Box>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                                    {ethnicity.map((eth, idx) => (
                                        <Box
                                            key={idx}
                                            sx={{
                                                bgcolor: theme[themeStyle].gray_text,
                                                borderRadius: 1,
                                                px: 1,
                                                py: 0.5,
                                                mr: 1,
                                                mb: 1,
                                                display: 'flex',
                                                alignItems: 'center',
                                            }}>
                                                <Typography
                                                    sx={{ fontSize: 12, color: theme[themeStyle].primary_white }}>
                                                {eth}
                                            </Typography>
                                            {editingSections.ethnicity && (
                                                <IconButton
                                                    size="small"
                                                    sx={{ ml: 0.2, p: 0.2, color: theme[themeStyle].highlight }}
                                                    onClick={() => {
                                                        const newEthnicity = ethnicity.filter((_, i) => i !== idx);
                                                        setEthnicity(newEthnicity);
                                                        onSubmit({ ethnicity: newEthnicity });
                                                    }}>
                                                    <Cancel fontSize="inherit" />
                                                </IconButton>
                                            )}
                                        </Box>
                                    ))}
                                </Box>
                                <Box sx={{ mt: 1 }}>
                                    <Autocomplete
                                        value={newEthnicity}
                                        onChange={(_, newValue) => {
                                            if (newValue && !ethnicity.includes(newValue)) {
                                                setEthnicity([...ethnicity, newValue]);
                                            }
                                        }}
                                        inputValue={newEthnicity}
                                        onInputChange={(_, newInputValue) => setNewEthnicity(newInputValue)}
                                        options={ETHNICITIES.filter(eth => !ethnicity.includes(eth))}
                                        popupIcon={<ArrowDropDown sx={{ color: theme[themeStyle].tag_white }} />}
                                        componentsProps={{
                                            paper: {
                                                sx: {
                                                    backgroundColor: theme[themeStyle].secondaryBg,
                                                    color: theme[themeStyle].primary_white,
                                                    '& .MuiAutocomplete-option': {
                                                        color: theme[themeStyle].primary_white,
                                                        '&:hover': {
                                                            backgroundColor: theme[themeStyle].highlight,
                                                        },
                                                        '&.Mui-focused': {
                                                                backgroundColor:
                                                                    theme[themeStyle].background_gradient.end,
                                                        },
                                                        '&.Mui-selected': {
                                                            backgroundColor: theme[themeStyle].primary_orange,
                                                            color: theme[themeStyle].black,
                                                        },
                                                    },
                                                },
                                            },
                                        }}
                                        renderInput={params => (
                                            <TextField
                                                {...params}
                                                size="small"
                                                label="Add Ethnicity"
                                                fullWidth
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        backgroundColor: theme[themeStyle].gray_text,
                                                        color: theme[themeStyle].tag_white,
                                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                        '& fieldset': {
                                                            borderColor: theme[themeStyle].placeholderGray50,
                                                        },
                                                        '&:hover fieldset': {
                                                            borderColor: theme[themeStyle].placeholderGray50,
                                                        },
                                                        '&.Mui-focused fieldset': {
                                                            borderColor: theme[themeStyle].highlight,
                                                        },
                                                    },
                                                    '& .MuiInputBase-input': {
                                                        color: theme[themeStyle].tag_white,
                                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                    },
                                                    '& .MuiInputLabel-root': {
                                                        color: theme[themeStyle].tag_white,
                                                    },
                                                }}
                                            />
                                        )}
                                    />
                                </Box>
                            </Box>
                        )}
                            {technicalLayout && renderInlineSectionActions('ethnicity')}
                    </Box>
                    <Divider sx={{ my: 2, borderColor: theme[themeStyle].grey }} />
                </>
            )}

            {/* Special Skills Section */}
            {shouldShowSection(
                profile,
                'special_skills',
                'special_skills',
                editingSections.special_skills,
                !!profile?.special_skills,
            ) &&
                !(artistMergedAboutMeta && !editingSections.special_skills) && (
                <>
                    <Box sx={{ mb: 2 }}>
                            <SectionHeader
                                title="Special Skills"
                                section="special_skills"
                                suppressActionsInEdit={technicalLayout}
                            />
                        {!editingSections.special_skills || previewMode ? (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
                                {specialSkills.length === 0 ? (
                                        <Typography
                                            sx={{
                                                fontSize: 13,
                                                color: technicalLayout
                                                    ? 'rgba(255,255,255,0.35)'
                                                    : theme[themeStyle].light_gray_text,
                                                fontStyle: technicalLayout ? 'italic' : 'normal',
                                                textTransform: technicalLayout ? 'capitalize' : 'none',
                                            }}>
                                        No special skills
                                    </Typography>
                                    ) : technicalLayout ? (
                                        <Typography
                                            sx={{
                                                fontSize: 14,
                                                color: theme[themeStyle].primary_white,
                                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                                textTransform: 'capitalize',
                                            }}>
                                            {specialSkills[0]}
                                    </Typography>
                                ) : (
                                    specialSkills.map((skill, idx) => (
                                        <Box
                                            key={idx}
                                            sx={{
                                                    bgcolor: '#20201f',
                                                    borderRadius: '999px',
                                                    px: 1.5,
                                                    py: 0.75,
                                                mr: 1,
                                                mb: 1,
                                                display: 'flex',
                                                alignItems: 'center',
                                                    border: '1px solid rgba(255,255,255,0.04)',
                                            }}>
                                            <Typography
                                                sx={{
                                                    fontSize: 12,
                                                        color: theme[themeStyle].highlight,
                                                    textTransform: 'capitalize',
                                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                                }}>
                                                {skill}
                                            </Typography>
                                            {editingSections.special_skills && (
                                                <IconButton
                                                    size="small"
                                                    sx={{ ml: 0.2, p: 0.2, color: theme[themeStyle].highlight }}
                                                    onClick={() =>
                                                        setSpecialSkills(specialSkills.filter((_, i) => i !== idx))
                                                    }>
                                                    <Cancel fontSize="inherit" />
                                                </IconButton>
                                            )}
                                        </Box>
                                    ))
                                )}
                            </Box>
                        ) : (
                            <Box>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                                    {specialSkills.map((skill, idx) => (
                                        <Box
                                            key={idx}
                                            sx={{
                                                    bgcolor: '#20201f',
                                                    borderRadius: '999px',
                                                    px: 1.5,
                                                    py: 0.75,
                                                mr: 1,
                                                mb: 1,
                                                display: 'flex',
                                                alignItems: 'center',
                                                    border: '1px solid rgba(255,255,255,0.04)',
                                            }}>
                                            <Typography
                                                sx={{
                                                    fontSize: 12,
                                                        color: theme[themeStyle].highlight,
                                                    textTransform: 'capitalize',
                                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                                }}>
                                                {skill}
                                            </Typography>
                                            {editingSections.special_skills && (
                                                <IconButton
                                                    size="small"
                                                    sx={{ ml: 0.2, p: 0.2, color: theme[themeStyle].highlight }}
                                                    onClick={() =>
                                                        setSpecialSkills(specialSkills.filter((_, i) => i !== idx))
                                                    }>
                                                    <Cancel fontSize="inherit" />
                                                </IconButton>
                                            )}
                                        </Box>
                                    ))}
                                </Box>
                                {!showAddSpecialSkillForm ? (
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<AddCircleOutline />}
                                        onClick={() => setShowAddSpecialSkillForm(true)}
                                        sx={{
                                            borderColor: theme[themeStyle].primary_orange,
                                            color: theme[themeStyle].primary_white,
                                            '&:hover': {
                                                borderColor: theme[themeStyle].primary_orange,
                                                backgroundColor: 'rgba(255, 140, 0, 0.1)',
                                            },
                                        }}>
                                        Add New Special Skill
                                    </Button>
                                ) : (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 1,
                                            mt: 1,
                                            p: 2,
                                            bgcolor: theme[themeStyle].gray_text,
                                            borderRadius: 1,
                                        }}>
                                        <Typography
                                            sx={{ fontSize: 14, color: theme[themeStyle].primary_white, mb: 1 }}>
                                            Add New Special Skill
                                        </Typography>
                                        <Grid container spacing={1}>
                                            <Grid item xs={12}>
                                                <TextField
                                                    size="small"
                                                    label="Special Skill"
                                                    value={newSpecialSkill}
                                                    InputLabelProps={{
                                                        sx: {
                                                            color: theme[themeStyle].tag_white,
                                                        },
                                                    }}
                                                    onChange={e => setNewSpecialSkill(e.target.value)}
                                                    fullWidth
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            backgroundColor: theme[themeStyle].gray_text,
                                                            color: theme[themeStyle].tag_white,
                                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                            '& fieldset': {
                                                                borderColor: theme[themeStyle].placeholderGray50,
                                                            },
                                                            '&:hover fieldset': {
                                                                borderColor: theme[themeStyle].placeholderGray50,
                                                            },
                                                            '&.Mui-focused fieldset': {
                                                                borderColor: theme[themeStyle].highlight,
                                                            },
                                                        },
                                                        '& .MuiInputBase-input': {
                                                            color: theme[themeStyle].tag_white,
                                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                        },
                                                    }}
                                                />
                                            </Grid>
                                        </Grid>
                                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 1 }}>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                onClick={() => {
                                                    setShowAddSpecialSkillForm(false);
                                                    setNewSpecialSkill('');
                                                }}>
                                                Cancel
                                            </Button>
                                            <Button
                                                size="small"
                                                variant="contained"
                                                onClick={() => {
                                                    if (newSpecialSkill) {
                                                        setSpecialSkills([...specialSkills, newSpecialSkill]);
                                                        setNewSpecialSkill('');
                                                        setShowAddSpecialSkillForm(false);
                                                    }
                                                }}>
                                                Add Skill
                                            </Button>
                                        </Box>
                                    </Box>
                                )}
                            </Box>
                        )}
                            {renderInlineSectionActions('special_skills')}
                    </Box>
                    <Divider sx={{ my: 2, borderColor: theme[themeStyle].grey }} />
                </>
            )}

            {/* Links Section */}
            {shouldShowSection(profile, 'links', 'links', editingSections.links, !!profile?.links) && (
                <>
                    <Box sx={{ mb: 2 }}>
                        <SectionHeader title="Links" section="links" suppressActionsInEdit={technicalLayout} />
                        {!editingSections.links || previewMode ? (
                            <Box>
                                {links.length === 0 ? (
                                    <Typography sx={{ fontSize: 13, color: theme[themeStyle].light_gray_text }}>
                                        No links
                                    </Typography>
                                ) : (
                                    links.map((l, idx) => (
                                        <Box key={idx} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                            <Typography
                                                sx={{ fontSize: 12, color: theme[themeStyle].primary_white, mr: 1 }}>
                                                <a
                                                    href={l.description}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    style={{
                                                        color: theme[themeStyle].highlight,
                                                        textDecoration: 'underline',
                                                    }}>
                                                    {l.link}
                                                </a>
                                                {l.description && (
                                                    <span
                                                        style={{
                                                            color: theme[themeStyle].light_gray_text,
                                                            marginLeft: 8,
                                                        }}>
                                                        ({l.description})
                                                    </span>
                                                )}
                                            </Typography>
                                            {editingSections.links && (
                                                <IconButton
                                                    size="small"
                                                    sx={{ ml: 0.2, p: 0.2, color: theme[themeStyle].highlight }}
                                                    onClick={() => setLinks(links.filter((_, i) => i !== idx))}>
                                                    <Cancel fontSize="inherit" />
                                                </IconButton>
                                            )}
                                        </Box>
                                    ))
                                )}
                            </Box>
                        ) : (
                            <Box>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                                    {links.map((l, idx) => (
                                            <Box key={idx} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                <Typography
                                                    sx={{
                                                        fontSize: 12,
                                                        color: theme[themeStyle].primary_white,
                                                        mr: 1,
                                                    }}>
                                                    <a
                                                        href={l.link}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        style={{
                                                            color: theme[themeStyle].highlight,
                                                            textDecoration: 'underline',
                                                        }}>
                                                        {l.link}
                                                    </a>
                                                    {l.description && (
                                                        <span
                                                            style={{
                                                                color: theme[themeStyle].light_gray_text,
                                                                marginLeft: 8,
                                                            }}>
                                                            ({l.description})
                                                        </span>
                                                    )}
                                                </Typography>
                                                {editingSections.links && (
                                                    <IconButton
                                                        size="small"
                                                        sx={{ ml: 0.2, p: 0.2, color: theme[themeStyle].highlight }}
                                                        onClick={() => setLinks(links.filter((_, i) => i !== idx))}>
                                                        <Cancel fontSize="inherit" />
                                                    </IconButton>
                                                )}
                                            </Box>
                                    ))}
                                </Box>
                                {!showAddLinkForm ? (
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<AddCircleOutline />}
                                        onClick={() => setShowAddLinkForm(true)}
                                        sx={{
                                            borderColor: theme[themeStyle].primary_orange,
                                            color: theme[themeStyle].primary_white,
                                            '&:hover': {
                                                borderColor: theme[themeStyle].primary_orange,
                                                backgroundColor: 'rgba(255, 140, 0, 0.1)',
                                            },
                                        }}>
                                        Add New Link
                                    </Button>
                                ) : (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 1,
                                            mt: 1,
                                            p: 2,
                                            bgcolor: theme[themeStyle].gray_text,
                                            borderRadius: 1,
                                        }}>
                                        <Typography
                                            sx={{ fontSize: 14, color: theme[themeStyle].primary_white, mb: 1 }}>
                                            Add New Link
                                        </Typography>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                gap: 1,
                                                alignItems: 'stretch',
                                                flexDirection: 'column',
                                            }}>
                                            <TextField
                                                size="small"
                                                label="Link"
                                                value={newLink.link}
                                                onChange={e => setNewLink(s => ({ ...s, link: e.target.value }))}
                                                sx={{
                                                    flex: 1,
                                                    '& .MuiOutlinedInput-root': {
                                                        '& fieldset': {
                                                            borderColor: theme[themeStyle].grey,
                                                        },
                                                        '&:hover fieldset': {
                                                            borderColor: theme[themeStyle].tag_white,
                                                        },
                                                    },
                                                }}
                                                InputProps={{
                                                    style: {
                                                        color: theme[themeStyle].tag_white,
                                                    },
                                                }}
                                                InputLabelProps={{
                                                    style: {
                                                        color: theme[themeStyle].grey,
                                                    },
                                                }}
                                            />
                                            <TextField
                                                size="small"
                                                label="Description"
                                                value={newLink.description}
                                                onChange={e => setNewLink(s => ({ ...s, description: e.target.value }))}
                                                sx={{
                                                    flex: 1,
                                                    '& .MuiOutlinedInput-root': {
                                                        '& fieldset': {
                                                            borderColor: theme[themeStyle].grey,
                                                        },
                                                        '&:hover fieldset': {
                                                            borderColor: theme[themeStyle].tag_white,
                                                        },
                                                    },
                                                }}
                                                InputProps={{
                                                    style: {
                                                        color: theme[themeStyle].tag_white,
                                                    },
                                                }}
                                                InputLabelProps={{
                                                    style: {
                                                        color: theme[themeStyle].grey,
                                                    },
                                                }}
                                            />
                                            <Button
                                                size="small"
                                                variant="contained"
                                                onClick={() => {
                                                    if (newLink.link) {
                                                        setLinks([...links, newLink]);
                                                        setNewLink({ link: '', description: '' });
                                                        setShowAddLinkForm(false);
                                                    }
                                                }}
                                                sx={{ width: '100%' }}>
                                                Add
                                            </Button>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                onClick={() => {
                                                    setShowAddLinkForm(false);
                                                    setNewLink({ link: '', description: '' });
                                                }}
                                                sx={{ width: '100%' }}>
                                                Cancel
                                            </Button>
                                        </Box>
                                    </Box>
                                )}
                            </Box>
                        )}
                        {renderInlineSectionActions('links')}
                    </Box>
                    <Divider sx={{ my: 2, borderColor: theme[themeStyle].grey }} />
                </>
            )}
        </Box>
    );
};

export default CastingDetails;
